<template>
  <div class="task-board-page">
    <div class="task-board-layout">
      <project-sidebar
        :projects="projectsForView"
        :tasks="allTasks"
        :count-tasks="sidebarCountTasks"
        :selected-project-id="selectedProjectId"
        :nav-mode="navMode"
        :current-user="currentUserData"
        :assignable-users="allAssignableUsers"
        :loading="loadingProjects"
        @nav-tasks="navigateToTasks"
        @nav-projects="navigateToProjects"
        @select-project="selectProject"
        @create-project="openCreateProjectModal"
        @edit-project="openEditProjectModal"
      />

      <projects-view
        v-if="navMode === 'projects'"
        class="task-board-main"
        :projects="projectsForView"
        :tasks="allTasks"
        :count-tasks="sidebarCountTasks"
        :selected-project-id="selectedProjectId"
        :current-user="currentUserData"
        :assignable-users="allAssignableUsers"
        :loading="loadingProjects"
        @select-project="selectProject"
        @create-project="openCreateProjectModal"
        @edit-project="openEditProjectModal"
        @add-member="handleAddProjectMember"
        @update-member="handleUpdateProjectMember"
        @remove-member="handleRemoveProjectMember"
        @assign-responsibility="handleAssignProjectResponsibility"
        @add-responsibility="handleAddProjectResponsibility"
      />

      <b-card v-else no-body class="task-board-main">
      <div class="task-board-toolbar">
        <div class="task-toolbar-main">
          <div class="task-toolbar-filters">
            <!--
              Scope toggle — managers with direct reports ONLY. Non-managers
              always see their own tasks (`scope` stays `'mine'`); showing a
              lone "My" chip when there is no Team alternative was redundant
              and confusing (see product feedback).
            -->
            <div
              v-if="isTaskManager"
              class="task-segmented-control task-scope-control"
              role="group"
              aria-label="Task scope"
            >
              <button
                type="button"
                class="task-segment-btn"
                :class="{ active: scope === 'mine' }"
                aria-label="My Tasks"
                @click="scope = 'mine'"
              >
                <feather-icon icon="UserIcon" size="14" />
                <span class="task-segment-label--full">My Tasks</span>
                <span class="task-segment-label--short">My</span>
              </button>
              <button
                type="button"
                class="task-segment-btn"
                :class="{ active: scope === 'team' }"
                aria-label="Team Tasks"
                @click="scope = 'team'"
              >
                <feather-icon icon="UsersIcon" size="14" />
                <span class="task-segment-label--full">Team Tasks</span>
                <span class="task-segment-label--short">Team</span>
              </button>
            </div>

            <div v-if="isTaskManager" class="task-toolbar-divider" aria-hidden="true" />

            <div class="task-filter-group">
              <span class="task-filter-label">Show</span>
              <div class="task-segmented-control task-due-control" role="group" aria-label="Due date filter">
                <button
                  type="button"
                  class="task-segment-btn"
                  :class="{ active: dueFocusFilter === 'today_overdue' && !dateRangeActive }"
                  aria-label="Today and overdue tasks"
                  @click="setDueFocusFilter('today_overdue')"
                >
                  <feather-icon icon="ClockIcon" size="14" />
                  <span class="task-segment-label--full">Today &amp; Overdue</span>
                  <span class="task-segment-label--short">Today</span>
                </button>
                <button
                  type="button"
                  class="task-segment-btn"
                  :class="{ active: dueFocusFilter === 'all' && !dateRangeActive }"
                  aria-label="All active tasks"
                  @click="setDueFocusFilter('all')"
                >
                  <span class="task-segment-label--full">All Active</span>
                  <span class="task-segment-label--short">All</span>
                </button>
              </div>
            </div>

            <div ref="dateRangeFilterRoot" class="task-date-range-filter">
              <button
                type="button"
                class="task-icon-btn"
                :class="{ 'task-icon-btn--active': dateRangeActive }"
                aria-label="Filter by date range"
                @click="dateRangeMenuOpen = !dateRangeMenuOpen"
              >
                <feather-icon icon="CalendarIcon" size="16" />
                <span v-if="dateRangeActive" class="task-date-range-dot" />
              </button>
              <div v-if="dateRangeMenuOpen" class="task-date-range-popover-body">
                <label for="task-date-range-filter" class="task-popover-label">Date range</label>
                <flat-pickr
                  id="task-date-range-filter"
                  v-model="dateRangeFilterValue"
                  :config="dateRangeFilterConfig"
                  class="form-control form-control-sm"
                  placeholder="Select a date range"
                />
                <b-button
                  v-if="dateRangeFilterValue"
                  size="sm"
                  variant="outline-secondary"
                  class="w-100 mt-50"
                  @click="dateRangeFilterValue = null"
                >
                  <feather-icon icon="XIcon" size="13" class="mr-25" />Clear range
                </b-button>
              </div>
            </div>
          </div>

          <div class="task-toolbar-actions">
            <div class="task-segmented-control task-view-control" role="group" aria-label="View mode">
              <button
                type="button"
                class="task-segment-btn task-segment-btn--icon"
                :class="{ active: view === 'kanban' }"
                aria-label="Board view"
                @click="view = 'kanban'"
              >
                <feather-icon icon="ColumnsIcon" size="16" />
              </button>
              <button
                type="button"
                class="task-segment-btn task-segment-btn--icon"
                :class="{ active: view === 'list' }"
                aria-label="List view"
                @click="view = 'list'"
              >
                <feather-icon icon="ListIcon" size="16" />
              </button>
            </div>

            <b-button variant="primary" size="sm" class="task-add-btn" @click="openCreateModal">
              <feather-icon icon="PlusIcon" size="15" class="mr-50" />New Task
            </b-button>
          </div>
        </div>

        <div class="task-toolbar-context">
          <feather-icon icon="FilterIcon" size="14" class="task-context-icon" />
          <span class="task-context-text">{{ toolbarContextSummary }}</span>
          <b-badge pill variant="light-primary" class="task-context-count">{{ displayTasks.length }}</b-badge>
        </div>
      </div>

      <div v-if="loading" class="task-loading-state">
        <b-spinner small variant="primary" class="mr-50" />
        <span>Loading tasks…</span>
      </div>

      <template v-else>
        <div v-if="!displayTasks.length" class="task-board-empty">
          <feather-icon icon="CheckSquareIcon" size="40" class="task-board-empty-icon" />
          <h5 class="task-board-empty-title">No tasks match your filters</h5>
          <p class="task-board-empty-hint">
            {{ emptyBoardHint }}
          </p>
          <div class="d-flex flex-wrap justify-content-center" style="gap: 0.5rem;">
            <b-button
              v-if="dueFocusFilter === 'today_overdue' && !dateRangeActive"
              size="sm"
              variant="outline-primary"
              @click="setDueFocusFilter('all')"
            >
              Show all active tasks
            </b-button>
            <b-button size="sm" variant="primary" @click="openCreateModal">
              <feather-icon icon="PlusIcon" size="14" class="mr-50" />Create a task
            </b-button>
          </div>
        </div>
        <task-kanban-board
          v-else-if="view === 'kanban'"
          :tasks="displayTasks"
          :all-tasks="allTasks"
          :current-user="currentUserData"
          :team-members="taskTeamMembers"
          :team-mode="scope === 'team'"
          :ping-revision="pingRevision"
          :projects="projects"
          @view="openEditModal"
          @edit="openEditModal"
          @delete="confirmDelete"
          @change-priority="handleChangePriority"
          @status-change="handleStatusChange"
          @reassign="handleReassign"
          @reorder="handleReorder"
          @quick-add="openCreateModalForAssignee"
          @ping="handlePingEmployee"
        />
        <task-list-view
          v-else-if="displayTasks.length"
          :tasks="displayTasks"
          :all-tasks="allTasks"
          :current-user="currentUserData"
          :team-members="taskTeamMembers"
          :show-assignee-column="scope === 'team'"
          :ping-revision="pingRevision"
          :projects="projects"
          @view="openEditModal"
          @edit="openEditModal"
          @delete="confirmDelete"
          @change-priority="handleChangePriority"
          @ping="handlePingEmployee"
        />
      </template>
    </b-card>
    </div>

    <task-form-modal
      v-model="showFormModal"
      :task="activeTask"
      :current-user="currentUserData"
      :team-members="taskTeamMembers"
      :submitting="submitting"
      :preset-assignee="presetAssignee"
      :projects="creatableProjectsList"
      :all-projects="projects"
      :preset-project-id="taskFormPresetProjectId"
      @submit="handleSubmit"
      @submit-error="showErrorToast"
    />

    <project-form-modal
      v-model="showProjectModal"
      :project="activeProject"
      :current-user="currentUserData"
      :assignable-users="allAssignableUsers"
      :submitting="submittingProject"
      @submit="handleProjectSubmit"
    />

    <b-modal
      id="task-delete-modal"
      ref="deleteModal"
      title="Delete Task"
      ok-title="Delete"
      ok-variant="danger"
      cancel-title="Cancel"
      @ok="submitDelete"
    >
      <p v-if="deleteTarget" class="mb-0">
        Delete task <strong>{{ deleteTarget.title }}</strong>? This cannot be undone.
      </p>
    </b-modal>
  </div>
</template>

<script>
import {
  BCard, BButton, BModal, BBadge, BSpinner,
} from 'bootstrap-vue'
import flatPickr from 'vue-flatpickr-component'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import taskService from '@/services/task-service'
import projectService from '@/services/project-service'
import userService from '@/services/user-service'
import notificationService from '@/services/notification-service'
import taskTeamMixin from './mixins/taskTeamMixin'
import TaskKanbanBoard from './components/TaskKanbanBoard.vue'
import TaskListView from './components/TaskListView.vue'
import TaskFormModal from './components/TaskFormModal.vue'
import ProjectSidebar from './components/ProjectSidebar.vue'
import ProjectsView from './components/ProjectsView.vue'
import ProjectFormModal from './components/ProjectFormModal.vue'
import { visibleProjects, creatableProjects, canCreateTaskInProject, canCreateProject, getProjectStatusDenialReason, inferProjectsFromTasks, canManageProject } from '@/utils/project-permissions'
import {
  PROJECT_ROLES,
  normalizeMembers,
  projectId as getProjectId,
  hasValidMemberResponsibility,
} from '@/utils/project-config'
import {
  buildTaskPayload, taskId, extractServerId, getEscalationLevel, TASK_ESCALATION_LEVELS, getDaysOverdue,
  isTaskOverdue, isDueToday, deriveCompletedAt, isArchived, taskMatchesDateRange,
  taskMatchesTodayOverdueFocus,
  taskEligibleForRecurringReminder,
  isRecurrenceDue, computeNextRecurrenceDate, formatDateOnly,
  taskIsRecurringTemplate, taskRecurringTaskId, normalizeTaskRecurrenceFields,
  findRecurringOccurrenceForDueDate, hasOpenRecurringOccurrence,
  buildHistoryEntries, appendHistoryEntries, normalizeChangeHistory,
  buildTaskFormData, mergeTaskAttachmentFields,
  taskAssigneeEmpId, taskOwnerEmpId, normalizeTaskStatus, isTaskComplete, TASK_STATUSES,
} from '@/utils/task-config'
import {
  visibleTasks,
  canDeleteTask,
  canEditTaskDetails,
  canConfigureRecurrence,
  canPingTask,
  canPingEmployee,
  getEditDenialReason,
  getStatusDenialReason,
  getPriorityDenialReason,
  getPingDenialReason,
  getReassignDenialReason,
  getEmpId,
} from '@/utils/task-permissions'
import { setColumnOrder } from '@/utils/task-order-store'
import { getLastNotifiedLevelIndex, recordNotifiedLevel, clearNotifiedLevel } from '@/utils/task-escalation-store'
import { wasReminderSent, recordReminderSent, clearReminderSent } from '@/utils/task-reminder-store'
import {
  recordTaskPing,
  recordEmployeePing,
  mergePingsOntoTasks,
  buildPingHistoryEntry,
  responseRetainsPing,
} from '@/utils/task-ping-store'
import { resolveUserIdFromEmpId, formatPushFailureMessage, FCM_PUSH_ONLY } from '@/utils/fcm-push'
import {
  mergeProjectResponsibilityTasks,
  isProjectResponsibilityTask,
  isOpenProjectResponsibilityTask,
  countOpenProjectResponsibilities,
  projectResponsibilityTaskId,
} from '@/utils/project-responsibility-tasks'
import { applyAddResponsibility } from '@/utils/project-store'

export default {
  name: 'TaskBoard',
  components: {
    BCard, BButton, BModal, BBadge, BSpinner,
    TaskKanbanBoard, TaskListView, TaskFormModal,
    ProjectSidebar, ProjectsView, ProjectFormModal, flatPickr,
  },
  mixins: [taskTeamMixin],
  data() {
    return {
      loading: false,
      submitting: false,
      scope: 'mine',
      view: 'kanban',
      allTasks: [],
      /**
       * Default board focus chip — `'today_overdue'` on load (due today OR
       * overdue, still open). `'all'` shows the broader active board
       * (upcoming + escalated, non-archived). Overridden while a date
       * range is picked (see `displayTasks`).
       */
      dueFocusFilter: 'today_overdue',
      /**
       * Raw `flat-pickr` binding for the date-range filter — a string like
       * `"2026-07-10 to 2026-07-16"` once a full range is picked, a single
       * `"2026-07-16"` while only the first day of a range has been
       * clicked so far, or `null` when cleared/untouched. Parsed by
       * `parsedDateRange` below; never touched directly outside the picker
       * itself and the "clear" button.
       */
      dateRangeFilterValue: null,
      dateRangeFilterConfig: {
        mode: 'range',
        dateFormat: 'Y-m-d',
        altInput: true,
        altFormat: 'd M Y',
        // Deliberately NO `static`/`appendTo` override — left in
        // flatpickr's default mode (calendar appended to `document.body`,
        // positioned by its own logic against the input's real viewport
        // coordinates), same as every other `flat-pickr` in this codebase.
        // See the template's doc comment on `.task-date-range-filter` for
        // why `static: true` (this field's previous setting, when the
        // picker briefly lived inside a `<b-dropdown>`) actively broke the
        // calendar's position instead of fixing anything.
      },
      /** Whether the hand-rolled date-range popover (`.task-date-range-popover-body`) is open — see the template's doc comment on `.task-date-range-filter` for why this isn't a `<b-dropdown>`. */
      dateRangeMenuOpen: false,
      showFormModal: false,
      activeTask: null,
      deleteTarget: null,
      /** Pre-selected assignee (emp_id) for the per-column "quick add" button — create mode only, cleared once the modal is used for anything else. */
      presetAssignee: null,
      /** Periodic re-check timer for due-date escalations — see `checkTaskEscalations()`. */
      escalationCheckIntervalId: null,
      /**
       * Recurring-template ids currently mid-generation (a `createTask()` +
       * follow-up `updateTask()` round-trip in flight) — see
       * `checkRecurringTasks()`. Purely an in-tab race guard: prevents the
       * SAME browser tab's 5-minute interval from re-firing generation for
       * a template whose previous generation attempt (same tick or an
       * earlier one) hasn't finished yet. Does NOT protect against two
       * different tabs/users generating the same overdue occurrence
       * simultaneously — see the doc above `checkRecurringTasks()` for why
       * that's an accepted gap in a client-only implementation.
       */
      recurringGenerationInFlight: new Set(),
      /**
       * Bumped after every in-app Ping so list/card badges re-read
       * `task-ping-store.js` (localStorage writes alone are not reactive).
       */
      pingRevision: 0,
      projects: [],
      loadingProjects: false,
      selectedProjectId: null,
      /** Primary sidebar navigation: task board vs project management. */
      navMode: 'tasks',
      showProjectModal: false,
      activeProject: null,
      submittingProject: false,
    }
  },
  computed: {
    projectsForView() {
      return inferProjectsFromTasks(this.projects, this.allTasks, this.currentUserData)
    },
    scopedTasks() {
      let tasks = visibleTasks(this.allTasks, this.currentUserData, this.taskTeamMembers, this.scope === 'team')
      if (this.selectedProjectId != null) {
        tasks = tasks.filter(t => {
          const pid = t.projectId ?? t.project_id
          return pid != null && String(pid) === String(this.selectedProjectId)
        })
      }
      return tasks
    },
    creatableProjectsList() {
      return creatableProjects(this.projectsForView, this.currentUserData, this.allTasks)
    },
    /**
     * Pre-fill Project on "New Task" when the user is filtering the task
     * board by a specific project — never on "All Tasks", and never for
     * stale/unknown project ids.
     */
    taskFormPresetProjectId() {
      if (this.navMode !== 'tasks' || this.selectedProjectId == null) return null
      const match = this.projectsForView.find(p => String(getProjectId(p)) === String(this.selectedProjectId))
      return match ? getProjectId(match) : null
    },
    /** Same scope as task assignment: logged-in user + direct reports only. */
    allAssignableUsers() {
      const seen = new Set()
      const list = []
      const add = u => {
        const id = String(u.emp_id ?? u.empId ?? u.id)
        if (!id || seen.has(id)) return
        seen.add(id)
        list.push(u)
      }
      add(this.currentUserData)
      ;(this.taskTeamMembers || []).forEach(add)
      return list
    },
    selectedProject() {
      if (this.selectedProjectId == null) return null
      return this.projectsForView.find(p => String(getProjectId(p)) === String(this.selectedProjectId)) || null
    },
    /**
     * Splits the raw `flat-pickr` range string into `{ from, to }` day
     * strings, or `null` when nothing's picked. Handles all 3 states the
     * underlying string can be in — see `dateRangeFilterValue`'s doc.
     */
    parsedDateRange() {
      const raw = this.dateRangeFilterValue
      if (!raw || typeof raw !== 'string') return null
      if (raw.includes(' to ')) {
        const [from, to] = raw.split(' to ').map(part => part.trim())
        return from ? { from, to: to || from } : null
      }
      return { from: raw, to: raw }
    },
    dateRangeActive() {
      return !!this.parsedDateRange
    },
    displayFilterCaption() {
      if (this.dateRangeActive) return 'Showing that range (incl. archived Done)'
      if (this.dueFocusFilter === 'today_overdue') return 'Showing today & overdue tasks'
      return 'Showing all active tasks'
    },
    /** Human-readable summary for the context bar below the toolbar. */
    toolbarContextSummary() {
      const parts = []
      if (this.isTaskManager) {
        parts.push(this.scope === 'team' ? 'Team tasks' : 'My tasks')
      }
      if (this.selectedProject) {
        parts.push(this.selectedProject.name)
      }
      if (this.dateRangeActive && this.parsedDateRange) {
        const { from, to } = this.parsedDateRange
        parts.push(from === to ? from : `${from} – ${to}`)
      } else if (this.dueFocusFilter === 'today_overdue') {
        parts.push('due today or overdue')
      } else {
        parts.push('all active')
      }
      return parts.join(' · ')
    },
    emptyBoardHint() {
      if (this.dateRangeActive) return 'Try a different date range or clear the filter.'
      if (this.dueFocusFilter === 'today_overdue') {
        const openProject = countOpenProjectResponsibilities(this.projects, this.currentEmpId)
        if (openProject) {
          return 'Nothing is due today or overdue. Switch to All Active to see project responsibilities and upcoming tasks.'
        }
        return 'Nothing is due today or overdue. Switch to All Active to see upcoming tasks.'
      }
      if (this.selectedProject) return 'This project has no active tasks yet.'
      if (this.scope === 'team') return 'Your team has no matching tasks right now.'
      return 'Create a task to get started, or adjust your filters.'
    },
    /**
     * No date range: active board filtered by `dueFocusFilter` —
     * `'today_overdue'` (default) or `'all'` — always excluding
     * auto-archived Done (see `isArchived()`). Date range picked:
     * bypasses focus + archive filters and shows due/completed matches
     * in that range (incl. archived Done). Applies to Board and List,
     * My and Team.
     */
    displayTasks() {
      if (this.dateRangeActive) {
        const { from, to } = this.parsedDateRange
        return this.scopedTasks.filter(task => taskMatchesDateRange(task, from, to))
      }
      return this.scopedTasks.filter(task => !isArchived(task) && this.isDefaultScopeTask(task))
    },
    /**
     * Scope-filtered tasks for sidebar / project-detail count badges.
     * Mirrors mine/team scope (and optional date range) but intentionally
     * skips the board's due-focus chip (`today_overdue` vs `all`) so
     * undated synthetic project-responsibility rows and upcoming tasks
     * still contribute to per-project totals.
     */
    sidebarCountTasks() {
      let tasks = visibleTasks(this.allTasks, this.currentUserData, this.taskTeamMembers, this.scope === 'team')
      if (this.dateRangeActive) {
        const { from, to } = this.parsedDateRange
        return tasks.filter(task => taskMatchesDateRange(task, from, to))
      }
      return tasks.filter(task => !isArchived(task))
    },
  },
  async created() {
    this.initViewFromPreference()
    await this.loadTaskTeamMembers()
    // Default view: Team Tasks for managers, My Tasks for everyone else.
    // Runs once after direct reports load; manual scope toggles during the
    // session are left untouched.
    if (this.isTaskManager) {
      this.scope = 'team'
    }
    await this.loadProjects()
    await this.loadTasks()
    this.openProjectFromRouteQuery()
    this.openTaskFromRouteQuery()
    this.checkTaskEscalations()
    this.checkTaskReminders()
    this.checkRecurringTasks()
    // Re-check periodically while the board stays open (e.g. overnight, or
    // across a day rollover) — `getEscalationLevel()` is a pure function of
    // "now", so a task can newly cross a tier's threshold purely from time
    // passing, with no task data changing at all; a page reload isn't the
    // only moment that can happen. 5 minutes is frequent enough to catch
    // that promptly without any meaningful load — this only iterates
    // already-loaded in-memory tasks, no network calls unless an email
    // actually needs sending (escalations) or a recurring template's next
    // occurrence has come due (recurrence generation).
    this.escalationCheckIntervalId = window.setInterval(() => {
      this.checkTaskEscalations()
      this.checkTaskReminders()
      this.checkRecurringTasks()
    }, 5 * 60 * 1000)
  },
  mounted() {
    // Capture-phase, document-wide listener backing the hand-rolled
    // date-range popover's "click outside to close" behavior — see
    // `isOutsideDateRangeFilter()`'s doc for why this replaced
    // `<b-dropdown>`'s own built-in outside-click handling.
    document.addEventListener('click', this.handleDocumentClickForDateRange, true)
  },
  beforeDestroy() {
    if (this.escalationCheckIntervalId != null) window.clearInterval(this.escalationCheckIntervalId)
    document.removeEventListener('click', this.handleDocumentClickForDateRange, true)
  },
  watch: {
    view(val) {
      try {
        sessionStorage.setItem('taskBoardViewPreference', val)
      } catch (e) {
        // non-blocking
      }
    },
  },
  methods: {
    /**
     * Restore board/list preference from sessionStorage, or default to List
     * on narrow viewports (first visit only) — four stacked status columns
     * on a phone is a long scroll; list view scans faster on small screens.
     * Team Kanban still works well stacked (one member per row) so users
     * can switch back to Board any time; choice persists for the session.
     */
    initViewFromPreference() {
      try {
        const saved = sessionStorage.getItem('taskBoardViewPreference')
        if (saved === 'kanban' || saved === 'list') {
          this.view = saved
          return
        }
      } catch (e) {
        // non-blocking
      }
      if (typeof window !== 'undefined' && window.innerWidth < 768) {
        this.view = 'list'
      }
    },
    toast(title, variant = 'success', icon = 'CheckIcon') {
      this.$toast({
        component: ToastificationContent,
        props: { title, icon, variant, timeout: 5000 },
      })
    },
    showErrorToast(message) {
      this.toast(message, 'danger', 'AlertTriangleIcon')
    },
    /**
     * True when `event`'s click target is OUTSIDE both the date-range
     * filter's own toggle+popover (`$refs.dateRangeFilterRoot`) AND any
     * live flatpickr calendar element — the calendar is appended to
     * `document.body` (flatpickr's default; see `dateRangeFilterConfig`'s
     * doc for why that default was restored), so it is NOT a DOM
     * descendant of `dateRangeFilterRoot` and would otherwise register as
     * "outside" the moment a day/range is clicked, closing the popover out
     * from under the picker before the click even registers with
     * flatpickr. `.flatpickr-calendar` is a stable class flatpickr itself
     * applies to every calendar instance it creates, so this check needs
     * no `ref`/instance bookkeeping of our own.
     */
    isOutsideDateRangeFilter(target) {
      const root = this.$refs.dateRangeFilterRoot
      if (root && root.contains(target)) return false
      if (target.closest && target.closest('.flatpickr-calendar')) return false
      return true
    },
    /** `document` click handler (see `mounted()`) closing the date-range popover on a genuine outside click — see `isOutsideDateRangeFilter()`'s doc. */
    handleDocumentClickForDateRange(event) {
      if (this.dateRangeMenuOpen && this.isOutsideDateRangeFilter(event.target)) {
        this.dateRangeMenuOpen = false
      }
    },
    /** Select Today & Overdue / All and clear any date range so the chip actually drives `displayTasks`. */
    setDueFocusFilter(filter) {
      this.dueFocusFilter = filter
      this.dateRangeFilterValue = null
      this.dateRangeMenuOpen = false
    },
    /**
     * Active-board membership when no date range is picked.
     * - `'today_overdue'` (default): open tasks whose due calendar day is
     *   today or earlier — `taskMatchesTodayOverdueFocus()` (includes
     *   overdue recurring occurrences; excludes undated rows, including
     *   synthetic project responsibilities with `dueDate: null`).
     * - `'all'`: broader board — open project responsibilities, upcoming
     *   (incl. no due date / future), OR any escalated/overdue open task.
     */
    isDefaultScopeTask(task) {
      if (this.dueFocusFilter === 'today_overdue') {
        return taskMatchesTodayOverdueFocus(task)
      }
      if (isOpenProjectResponsibilityTask(task)) return true
      return !isTaskOverdue(task) || !!getEscalationLevel(task)
    },
    syncProjectResponsibilityTasks() {
      this.allTasks = mergeProjectResponsibilityTasks(this.allTasks, this.projects)
    },
    async syncMemberResponsibilityFromTask(task, completed) {
      const pid = task?.projectId ?? task?.project_id
      const memberEmpId = task?.projectMemberEmpId ?? taskAssigneeEmpId(task)
      if (pid == null || memberEmpId == null) return
      const project = this.projects.find(p => String(getProjectId(p)) === String(pid))
      if (!project) return
      const member = normalizeMembers(project.members).find(m => String(m.empId) === String(memberEmpId))
      if (!member) return
      const res = await projectService.updateMember(pid, {
        ...member,
        responsibilityCompleted: !!completed,
        responsibilityCompletedAt: completed ? new Date().toISOString() : null,
      }, project)
      if (res?.status === 'ERROR') {
        this.showErrorToast(res.message || 'Could not update project responsibility.')
        return
      }
      this.upsertLocalProject(res.data || project)
      this.syncProjectResponsibilityTasks()
    },
    normalizeTasksResponse(res) {
      let rows = []
      if (Array.isArray(res)) rows = res
      else if (res && Array.isArray(res.data)) rows = res.data
      else if (res && res.status === 'OK' && Array.isArray(res.data)) rows = res.data
      // Collapse camelCase/snake_case recurrence aliases once on load so
      // `recurrenceLastGeneratedDate` (and friends) are readable even when
      // the API only echoes snake_case — without this, every refresh looked
      // like the series had never generated and POSTed another occurrence.
      return rows.map(normalizeTaskRecurrenceFields)
    },
    /**
     * Upsert active pings + escalations into the navbar inbox for the
     * logged-in user. Safe no-op when Vuex/notifications isn't ready.
     */
    syncNotificationInboxFromTasks() {
      const myId = this.currentEmpId
      if (myId == null) return
      const empKey = String(myId)
      const relevant = (this.allTasks || []).filter(t => {
        const assignee = taskAssigneeEmpId(t)
        const owner = taskOwnerEmpId(t)
        return (assignee != null && String(assignee) === empKey)
          || (owner != null && String(owner) === empKey)
      })
      try {
        this.$store.dispatch('notifications/ingestTaskPings', {
          empId: myId,
          tasks: relevant,
        })
      } catch (e) {
        // non-blocking
      }
    },
    async loadTasks() {
      this.loading = true
      try {
        // Fetch everything the "My Tasks" / "My Team's Tasks" toggle could
        // ever need in one call (toggling `scope` re-filters client-side via
        // `displayTasks`/`visibleTasks()` rather than re-fetching) — so the
        // fetch-scope sent to the server is 'team' whenever the caller has
        // direct reports, 'mine' otherwise. See the contract doc at the top
        // of `task-service.js` for the query params this sends.
        // Team list: only scope + empId + managerId. Backend derives reports
        // from mgr_id; do not send teamMemberIds (ignored server-side, long URL → 500).
        const res = await taskService.getTasks({
          scope: this.isTaskManager ? 'team' : 'mine',
          empId: this.currentEmpId,
          managerId: this.isTaskManager ? this.currentEmpId : null,
        })
        if (res && res.status === 'SERVICE_UNAVAILABLE') {
          this.allTasks = []
          this.showErrorToast(res.message || 'Task service unavailable. Please contact admin.')
        } else if (res && (res.status === 401 || res.status === 403)) {
          window.sessionStorage.setItem('lastPage', window.location.href.split('/#')[1])
          localStorage.clear()
          this.$router.push({ name: 'auth-login' })
        } else if (
          res
          && (res.status === 'ERROR' || (res.httpStatus != null && res.httpStatus >= 400))
        ) {
          this.allTasks = []
          this.showErrorToast(res.message || 'Could not load tasks. Please try again.')
        } else {
          // Overlay optimistic local ping badges; cross-user delivery uses
          // API fields / changeHistory ping entries (see persistTaskPingToServer).
          // Assignee toasts: App.vue poller reads server tasks, not this store.
          this.allTasks = mergePingsOntoTasks(this.normalizeTasksResponse(res))
          this.syncProjectResponsibilityTasks()
          // Keep navbar bell in sync when the board loads (pings + escalations).
          this.syncNotificationInboxFromTasks()
        }
      } catch (err) {
        console.error('[task] load error:', err)
        this.allTasks = []
        this.showErrorToast('Could not load tasks. Please try again.')
      } finally {
        this.syncProjectResponsibilityTasks()
        this.loading = false
      }
    },
    async loadProjects() {
      this.loadingProjects = true
      try {
        const res = await projectService.getProjects({ empId: this.currentEmpId })
        if (res && res.status === 'OK') {
          this.projects = (res.data || []).map(p => projectService.normalizeProject(p)).filter(Boolean)
          this.syncProjectResponsibilityTasks()
        } else {
          this.projects = []
        }
      } catch (err) {
        console.warn('[task] loadProjects error:', err)
        this.projects = []
      } finally {
        this.loadingProjects = false
      }
    },
    navigateToTasks() {
      this.navMode = 'tasks'
      this.selectProject(null)
    },
    navigateToProjects() {
      this.navMode = 'projects'
      if (this.selectedProjectId == null) {
        const visible = visibleProjects(this.projectsForView, this.currentUserData, this.allTasks)
        if (visible.length) {
          this.selectedProjectId = getProjectId(visible[0])
        }
      }
      this.syncProjectRouteQuery()
    },
    selectProject(projectId) {
      this.selectedProjectId = projectId
      if (projectId != null && this.navMode === 'tasks') {
        this.navMode = 'tasks'
      }
      this.syncProjectRouteQuery()
    },
    syncProjectRouteQuery() {
      const query = { ...this.$route.query }
      if (this.navMode === 'projects') {
        query.view = 'projects'
        if (this.selectedProjectId != null) {
          query.projectId = String(this.selectedProjectId)
        } else {
          delete query.projectId
        }
      } else {
        delete query.view
        if (this.selectedProjectId != null) {
          query.projectId = String(this.selectedProjectId)
        } else {
          delete query.projectId
        }
      }
      this.$router.replace({ query }).catch(() => {})
    },
    openProjectFromRouteQuery() {
      const view = this.$route?.query?.view
      const rawId = this.$route?.query?.projectId
      if (view === 'projects') {
        this.navMode = 'projects'
        if (rawId != null) {
          const match = visibleProjects(this.projectsForView, this.currentUserData, this.allTasks)
            .find(p => String(getProjectId(p)) === String(rawId))
          if (match) this.selectedProjectId = getProjectId(match)
        } else {
          const visible = visibleProjects(this.projectsForView, this.currentUserData, this.allTasks)
          if (visible.length) {
            this.selectedProjectId = getProjectId(visible[0])
          }
        }
        return
      }
      if (rawId == null) return
      const match = visibleProjects(this.projectsForView, this.currentUserData, this.allTasks)
        .find(p => String(getProjectId(p)) === String(rawId))
      if (match) {
        this.selectedProjectId = getProjectId(match)
        this.navMode = 'tasks'
      }
    },
    openCreateProjectModal() {
      if (!canCreateProject()) {
        this.showErrorToast('You do not have permission to create projects.')
        return
      }
      this.activeProject = null
      this.showProjectModal = true
    },
    openEditProjectModal(project) {
      this.activeProject = project
      this.showProjectModal = true
    },
    upsertLocalProject(project) {
      const id = getProjectId(project)
      if (id == null) return
      const idx = this.projects.findIndex(p => String(getProjectId(p)) === String(id))
      const normalized = projectService.normalizeProject(project)
      if (idx >= 0) this.$set(this.projects, idx, normalized)
      else this.projects.push(normalized)
      if (this.activeProject && String(getProjectId(this.activeProject)) === String(id)) {
        this.activeProject = normalized
      }
    },
    /**
     * Update project status from the task form (PM only).
     * Reuses `projectService.updateProject` and preserves members/responsibilities.
     * @returns {Promise<boolean>} true when the update succeeded
     */
    async applyProjectStatusUpdate({ projectId: pid, status } = {}) {
      if (pid == null || !status) return false
      const project = this.projects.find(p => String(getProjectId(p)) === String(pid))
      if (!project) {
        this.showErrorToast('Project not found.')
        return false
      }
      if (!canManageProject(this.currentUserData, project)) {
        this.showErrorToast('Only the project manager can update project status.')
        return false
      }
      const payload = {
        ...projectService.buildPayload({
          id: pid,
          name: project.name,
          description: project.description || '',
          status,
          color: project.color,
          createdBy: project.createdBy ?? project.created_by,
          createdByName: project.createdByName ?? project.created_by_name,
        }, this.currentUserData),
        members: project.members || [],
        ...(Array.isArray(project.responsibilities)
          ? { responsibilities: project.responsibilities }
          : {}),
      }
      const res = await projectService.updateProject(pid, payload)
      if (res?.status === 'ERROR') {
        this.showErrorToast(res.message || 'Failed to update project status.')
        return false
      }
      this.upsertLocalProject(res.data || { ...project, ...payload, id: pid })
      this.syncProjectResponsibilityTasks()
      this.toastProjectPersistResult(`Project status set to ${status}`, res)
      return true
    },
    async handleProjectSubmit(form) {
      this.submittingProject = true
      try {
        const payload = projectService.buildPayload(form, this.currentUserData)
        if (form.mode === 'edit' && form.id != null) {
          const pid = form.id
          let project = this.activeProject || {}
          const res = await projectService.updateProject(pid, {
            ...payload,
            members: project.members || [],
            ...(Array.isArray(project.responsibilities)
              ? { responsibilities: project.responsibilities }
              : {}),
          })
          if (res?.status === 'ERROR') {
            this.showErrorToast(res.message || 'Failed to update project.')
            return
          }
          project = projectService.normalizeProject(res.data || {
            ...payload,
            id: pid,
            members: project.members || [],
            ...(Array.isArray(project.responsibilities)
              ? { responsibilities: project.responsibilities }
              : {}),
          })
          this.upsertLocalProject(project)

          const membersToRemove = form.membersToRemove || []
          for (const empId of membersToRemove) {
            const member = normalizeMembers(project.members).find(m => String(m.empId) === String(empId))
            const removeRes = await projectService.removeMember(pid, empId, project)
            if (removeRes?.status === 'ERROR') {
              this.showErrorToast(removeRes.message || 'Failed to remove team member.')
              return
            }
            project = projectService.normalizeProject(removeRes.data || project)
            this.upsertLocalProject(project)
            if (member?.name) {
              this.toastProjectPersistResult(`${member.name} removed from project`, removeRes)
            }
          }

          const membersToUpdate = form.membersToUpdate || []
          for (const member of membersToUpdate) {
            const prior = normalizeMembers(project.members).find(m => String(m.empId) === String(member?.empId))
            const hadResponsibility = hasValidMemberResponsibility(prior)
            const updateRes = await projectService.updateMember(pid, member, project)
            if (updateRes?.status === 'ERROR') {
              this.showErrorToast(updateRes.message || 'Failed to update team member.')
              return
            }
            project = projectService.normalizeProject(updateRes.data || project)
            this.upsertLocalProject(project)
            if (!hadResponsibility && member?.empId != null && hasValidMemberResponsibility(member)) {
              this.notifyProjectResponsibilityAssignees(pid, [member.empId])
            }
          }

          const membersToAdd = form.membersToAdd || []
          for (const member of membersToAdd) {
            const addRes = await projectService.addMember(
              pid,
              member,
              getEmpId(this.currentUserData),
              project,
            )
            if (addRes?.status === 'ERROR') {
              this.showErrorToast(addRes.message || 'Failed to add team member.')
              return
            }
            project = projectService.normalizeProject(addRes.data || project)
            this.upsertLocalProject(project)
            if (member?.empId != null && hasValidMemberResponsibility(member)) {
              this.notifyProjectResponsibilityAssignees(pid, [member.empId])
            }
          }

          const responsibilitiesToAdd = form.responsibilitiesToAdd || []
          const allNewAssignees = []
          for (const resp of responsibilitiesToAdd) {
            const addRespRes = await projectService.addResponsibilityToMembers(pid, resp.targetEmpIds, {
              responsibilityTitle: resp.responsibilityTitle,
              responsibilityDescription: resp.responsibilityDescription,
              attachments: resp.attachments || [],
              memberDefaults: resp.memberDefaults || {},
            }, project)
            if (addRespRes?.status === 'ERROR') {
              this.showErrorToast(addRespRes.message || 'Failed to add responsibility.')
              return
            }
            project = projectService.normalizeProject(addRespRes.data || project)
            this.upsertLocalProject(project)
            allNewAssignees.push(...(resp.targetEmpIds || []))
          }

          this.syncProjectResponsibilityTasks()
          if (allNewAssignees.length) {
            this.notifyProjectResponsibilityAssignees(pid, allNewAssignees)
          }
          this.toast('Project updated')
        } else {
          const empId = getEmpId(this.currentUserData)
          const displayName = `${this.currentUserData?.first_name || ''} ${this.currentUserData?.last_name || ''}`.trim()
            || this.currentUserData?.title
            || 'Unknown'
          const creatorMember = empId ? {
            empId: String(empId),
            name: displayName,
            role: PROJECT_ROLES.PROJECT_MANAGER,
            addedAt: new Date().toISOString(),
          } : null
          const extraMembers = normalizeMembers(form.members || [])
            .filter(m => !creatorMember || String(m.empId) !== String(creatorMember.empId))
          let withCreator = {
            ...payload,
            members: creatorMember
              ? [creatorMember, ...extraMembers.map(m => ({
                ...m,
                addedAt: m.addedAt || new Date().toISOString(),
                addedBy: empId != null ? String(empId) : null,
              }))]
              : extraMembers,
          }
          const pendingResponsibilities = form.responsibilities || []
          for (const resp of pendingResponsibilities) {
            withCreator = applyAddResponsibility(withCreator, resp.targetEmpIds, {
              responsibilityTitle: resp.responsibilityTitle,
              responsibilityDescription: resp.responsibilityDescription,
              attachments: resp.attachments || [],
              memberDefaults: resp.memberDefaults || {},
            })
          }
          const res = await projectService.createProject(withCreator)
          if (res?.status === 'ERROR') {
            this.showErrorToast(res.message || 'Failed to create project.')
            return
          }
          const created = projectService.normalizeProject(
            projectService.mergeWithComputed(res.data, withCreator),
          )
          this.upsertLocalProject(created)
          this.selectedProjectId = getProjectId(created)
          this.navMode = 'projects'
          this.syncProjectRouteQuery()
          this.syncProjectResponsibilityTasks()
          const allAssignees = pendingResponsibilities.flatMap(r => r.targetEmpIds || [])
          if (allAssignees.length) {
            this.notifyProjectResponsibilityAssignees(getProjectId(created), allAssignees)
          }
          this.toastProjectPersistResult('Project created', res)
        }
        this.showProjectModal = false
      } catch (err) {
        console.error('[task] project save error:', err)
        this.showErrorToast('Could not save project.')
      } finally {
        this.submittingProject = false
      }
    },
    toastProjectPersistResult(successMessage, res) {
      if (res?.source === 'local') {
        this.toast(`${successMessage} (saved locally only — not in database)`, 'warning', 'AlertTriangleIcon')
        return
      }
      this.toast(successMessage)
    },
    async handleAddProjectMember(member) {
      const project = this.activeProject || this.selectedProject
      const pid = getProjectId(project)
      if (!pid) return
      const res = await projectService.addMember(pid, member, getEmpId(this.currentUserData), project)
      if (res?.status === 'ERROR') {
        this.showErrorToast(res.message || 'Failed to add member.')
        return
      }
      this.upsertLocalProject(res.data || project)
      this.syncProjectResponsibilityTasks()
      // New member joined with a responsibility → same assign notify as standalone tasks.
      if (member?.empId != null && hasValidMemberResponsibility(member)) {
        this.notifyProjectResponsibilityAssignees(pid, [member.empId])
      }
      this.toastProjectPersistResult(`${member.name} added to project`, res)
    },
    async handleUpdateProjectMember(member) {
      const project = this.activeProject || this.selectedProject
      const pid = getProjectId(project)
      if (!pid) return
      const prior = normalizeMembers(project?.members).find(m => String(m.empId) === String(member?.empId))
      const hadResponsibility = hasValidMemberResponsibility(prior)
      const res = await projectService.updateMember(pid, member, project)
      if (res?.status === 'ERROR') {
        this.showErrorToast(res.message || 'Failed to update member.')
        return
      }
      this.upsertLocalProject(res.data || project)
      this.syncProjectResponsibilityTasks()
      // Only on first assignment of a responsibility — not title/description tweaks.
      if (!hadResponsibility && member?.empId != null && hasValidMemberResponsibility(member)) {
        this.notifyProjectResponsibilityAssignees(pid, [member.empId])
      }
      this.toastProjectPersistResult('Team member updated', res)
    },
    async handleAssignProjectResponsibility({ sourceEmpId, targetEmpIds, responsibility = null }) {
      const project = this.activeProject || this.selectedProject
      const pid = getProjectId(project)
      if (!pid || !sourceEmpId || !targetEmpIds?.length) return
      const res = await projectService.assignResponsibilityToMembers(
        pid,
        sourceEmpId,
        targetEmpIds,
        responsibility,
        project,
      )
      if (res?.status === 'ERROR') {
        this.showErrorToast(res.message || 'Failed to assign responsibility.')
        return
      }
      this.upsertLocalProject(res.data || project)
      this.syncProjectResponsibilityTasks()
      this.notifyProjectResponsibilityAssignees(pid, targetEmpIds)
      const count = targetEmpIds.length
      this.toastProjectPersistResult(
        `Responsibility assigned to ${count} team member${count === 1 ? '' : 's'}`,
        res,
      )
    },
    async handleAddProjectResponsibility({
      targetEmpIds, responsibilityTitle, responsibilityDescription, attachments = [], memberDefaults = {},
    }) {
      const project = this.activeProject || this.selectedProject
      const pid = getProjectId(project)
      if (!pid || !targetEmpIds?.length) return
      const res = await projectService.addResponsibilityToMembers(pid, targetEmpIds, {
        responsibilityTitle,
        responsibilityDescription,
        attachments,
        memberDefaults,
      }, project)
      if (res?.status === 'ERROR') {
        this.showErrorToast(res.message || 'Failed to add responsibility.')
        return
      }
      this.upsertLocalProject(res.data || project)
      this.syncProjectResponsibilityTasks()
      this.notifyProjectResponsibilityAssignees(pid, targetEmpIds)
      const count = targetEmpIds.length
      this.toastProjectPersistResult(
        `Task added for ${count} team member${count === 1 ? '' : 's'}`,
        res,
      )
    },
    /**
     * Fire-and-forget assignment notify for project responsibility targets.
     * Reuses `notifyAssignment` → `taskService.notifyTaskAssigned` (FCM
     * `task_assigned` via POST /api/notify/push + assignment email). Skips
     * self; looks up the synthetic `proj-resp-*` task after sync.
     */
    notifyProjectResponsibilityAssignees(projectIdValue, empIds) {
      if (projectIdValue == null || !empIds?.length) return
      const unique = [...new Set(empIds.map(id => String(id)).filter(Boolean))]
      unique.forEach(empId => {
        const syntheticId = projectResponsibilityTaskId(projectIdValue, empId)
        const task = (this.allTasks || []).find(t => String(taskId(t)) === String(syntheticId))
          || {
            id: syntheticId,
            title: 'Project responsibility',
            assignedTo: empId,
            projectId: projectIdValue,
            source: 'project-responsibility',
            createdBy: this.currentEmpId,
          }
        this.notifyAssignment(task, empId)
      })
    },
    async handleRemoveProjectMember(member) {
      const project = this.activeProject || this.selectedProject
      const pid = getProjectId(project)
      if (!pid) return
      const res = await projectService.removeMember(pid, member.empId, project)
      if (res?.status === 'ERROR') {
        this.showErrorToast(res.message || 'Failed to remove member.')
        return
      }
      this.upsertLocalProject(res.data || project)
      this.syncProjectResponsibilityTasks()
      this.toastProjectPersistResult(`${member.name} removed from project`, res)
    },
    openCreateModal() {
      this.activeTask = null
      this.presetAssignee = null
      this.showFormModal = true
    },
    /** "Quick add" — the small + button on a team member's column header in the per-member Kanban board. */
    openCreateModalForAssignee(empId) {
      this.activeTask = null
      this.presetAssignee = empId != null ? String(empId) : null
      this.showFormModal = true
    },
    openEditModal(task) {
      this.activeTask = task
      this.presetAssignee = null
      this.showFormModal = true
    },
    /**
     * Deep-link support — `?taskId=<id>` in the URL opens straight to that
     * task's Edit modal, e.g. from the clickable "View Task" button in the
     * assignment/escalation emails (`task-service.js#buildTaskDeepLink`).
     * Looks the id up against `visibleTasks(..., includeTeam=true)` (not
     * the current `scope` toggle's filtered list) so a manager clicking a
     * link for a report's task finds it regardless of which tab happens
     * to be selected — "anyone who can view a task" per the permission
     * model, same as the History panel. Silently no-ops if the id isn't
     * found (deleted task, stale link, or simply not visible to this
     * user) — a stale email link should never surface a scary error.
     */
    openTaskFromRouteQuery() {
      const rawId = this.$route?.query?.taskId
      if (rawId == null) return
      const candidates = visibleTasks(this.allTasks, this.currentUserData, this.taskTeamMembers, true)
      const match = candidates.find(t => String(taskId(t)) === String(rawId))
      if (match) this.openEditModal(match)
    },
    confirmDelete(task) {
      if (!canDeleteTask(this.currentUserData, task)) {
        this.showErrorToast('You can only delete tasks you own or created.')
        return
      }
      this.deleteTarget = task
      this.$refs.deleteModal.show()
    },
    async submitDelete(bvModalEvt) {
      bvModalEvt.preventDefault()
      if (!this.deleteTarget) return
      const id = taskId(this.deleteTarget)
      try {
        const res = await taskService.archiveTask(id)
        if (res && res.status === 'ERROR') {
          this.showErrorToast(res.message || 'Failed to delete task.')
          return
        }
        this.allTasks = this.allTasks.filter(t => taskId(t) !== id)
        this.toast('Task deleted', 'success', 'Trash2Icon')
        this.$refs.deleteModal.hide()
      } catch (err) {
        console.error('[task] delete error:', err)
        this.showErrorToast('Could not delete task. Please try again.')
      }
    },
    /** Resolves a display name for `assigneeEmpId` — self, a known direct report, or falls back to whatever `existing` already had. */
    resolveAssigneeName(assigneeEmpId, existing) {
      if (assigneeEmpId == null) return existing?.assignedToName || null
      const myEmpId = this.currentUserData?.emp_id ?? this.currentUserData?.empId
      if (String(assigneeEmpId) === String(myEmpId)) {
        return `${this.currentUserData?.first_name || ''} ${this.currentUserData?.last_name || ''}`.trim()
          || this.currentUserData?.title || null
      }
      const teamName = this.taskTeamMemberName(assigneeEmpId)
      if (teamName) return teamName
      if (existing && String(existing.assignedTo) === String(assigneeEmpId)) return existing.assignedToName || null
      return null
    },
    /**
     * Fire-and-forget task-assignment email trigger — see
     * `task-service.js#notifyTaskAssigned` for the actual send/resilience
     * contract. Intentionally NOT awaited by any caller: this must never
     * block or delay the create/edit/reassign UI flow (per spec). Skips
     * self-assignment entirely (no value in emailing yourself about your
     * own task). `async` only to resolve the manager cc below before
     * sending — still never awaited by ITS OWN callers, same
     * fire-and-forget contract as before.
     */
    async notifyAssignment(task, assigneeEmpId) {
      if (assigneeEmpId == null || String(assigneeEmpId) === String(this.currentEmpId)) return
      // Cc the assigning manager — per product direction, every task email
      // must cc the manager (`task.createdBy`) and NEVER an HR address; see
      // `resolveManagerEmail()`'s doc below for the resolution strategy.
      const assignedByEmail = await this.resolveManagerEmail(task.createdBy, assigneeEmpId)
      taskService.notifyTaskAssigned({
        task,
        assigneeEmail: this.taskTeamMemberEmail(assigneeEmpId),
        assigneeName: this.taskTeamMemberName(assigneeEmpId),
        assignedByName: `${this.currentUserData?.first_name || ''} ${this.currentUserData?.last_name || ''}`.trim()
          || this.currentUserData?.title || 'Your manager',
        assignedByEmail,
        assigneeEmpId,
        assigneeUserId: this.taskTeamMemberUserId(assigneeEmpId),
      })
    },
    /** Recipient email for `assigneeEmpId` — self (`currentUserData.email`, same `users` row shape `auth-service.js` reads) or a known direct report (`taskTeamMemberEmail`). `null` if neither resolves, same "skip gracefully" contract as `taskTeamMemberEmail` itself. */
    resolveAssigneeEmail(assigneeEmpId) {
      if (assigneeEmpId == null) return null
      if (String(assigneeEmpId) === String(this.currentEmpId)) return this.currentUserData?.email || null
      return this.taskTeamMemberEmail(assigneeEmpId)
    },
    /**
     * General-purpose "resolve this emp_id's own email" — same 3-tier
     * strategy as `resolveManagerEmail()` below (self / known direct
     * report / `userService.getUserDetail()` fallback), but WITHOUT that
     * method's "is this person even a different person from the
     * assignee?" guard, since callers here (`notifyTaskUpdated`) may
     * legitimately want either party's email, not specifically "the
     * manager, if distinct from the assignee". Used for the `TASK_UPDATED`
     * notification's recipient resolution, where the recipient can be
     * EITHER the assignee OR the creator/manager depending on who made the
     * edit — see `notifyTaskUpdated()`'s own doc for that routing.
     */
    async resolveEmpEmail(empId) {
      if (empId == null) return null
      if (String(empId) === String(this.currentEmpId)) return this.currentUserData?.email || null
      const teamEmail = this.taskTeamMemberEmail(empId)
      if (teamEmail) return teamEmail
      try {
        const res = await userService.getUserDetail(empId)
        if (res && res.status === 'OK' && res.data) return res.data.email || null
      } catch (err) {
        console.warn('[task] emp email lookup failed (non-blocking):', err)
      }
      return null
    },
    /**
     * Manager for task update push — project PM when the task belongs to a
     * project, otherwise the assigning manager (`createdBy`).
     */
    resolveTaskManagerEmpId(task) {
      if (!task) return null
      const project = this.projectForTask(task)
      if (project) {
        const pm = normalizeMembers(project.members).find(
          m => m.role === PROJECT_ROLES.PROJECT_MANAGER && m.empId != null,
        )
        if (pm?.empId != null) return pm.empId
      }
      return task.createdBy ?? null
    },
    /**
     * Fires push-only "task updated" notification to the task manager — see
     * `task-service.js#notifyTaskUpdated`. Called from `recordTaskHistory()`
     * with the same `entries` diff as the History panel. Status → Done is
     * routed to `notifyTaskCompleted()` instead (email to relevant parties).
     * Skips when the manager is the current editor (no self-push).
     */
    async notifyTaskUpdated(currentTask, entries) {
      if (!entries || !entries.length) return
      const statusEntry = entries.find(e => e.field === 'status')
      const becameDone = statusEntry
        && normalizeTaskStatus({ status: statusEntry.newValue }) === TASK_STATUSES.DONE
        && normalizeTaskStatus({ status: statusEntry.oldValue }) !== TASK_STATUSES.DONE
      if (becameDone) {
        this.notifyTaskCompleted(currentTask)
        return
      }
      const managerEmpId = this.resolveTaskManagerEmpId(currentTask)
      if (managerEmpId == null || String(managerEmpId) === String(this.currentEmpId)) return

      taskService.notifyTaskUpdated({
        task: currentTask,
        changedByName: `${this.currentUserData?.first_name || ''} ${this.currentUserData?.last_name || ''}`.trim()
          || this.currentUserData?.title || 'A team member',
        managerEmpId,
        managerUserId: this.taskTeamMemberUserId(managerEmpId),
      })
    },
    /**
     * Task marked Done — email (+ FCM push) to creator, project PM, and
     * assignee when each is distinct from the completer. Project
     * responsibility tasks use the same path from `handleStatusChange`.
     */
    async notifyTaskCompleted(task) {
      if (!task) return
      const completedByName = `${this.currentUserData?.first_name || ''} ${this.currentUserData?.last_name || ''}`.trim()
        || this.currentUserData?.title || 'A team member'
      const creatorEmpId = task.createdBy
      const managerEmpId = this.resolveTaskManagerEmpId(task)
      const assigneeEmpId = task.assignedTo
      const completerKey = this.currentEmpId != null ? String(this.currentEmpId) : null

      const candidateEmpIds = []
      const addCandidate = (empId) => {
        if (empId == null) return
        const key = String(empId)
        if (completerKey && key === completerKey) return
        if (!candidateEmpIds.includes(key)) candidateEmpIds.push(key)
      }
      addCandidate(creatorEmpId)
      addCandidate(managerEmpId)
      addCandidate(assigneeEmpId)

      const recipients = []
      for (const empId of candidateEmpIds) {
        const email = await this.resolveEmpEmail(empId)
        if (!email) continue
        let name = this.taskTeamMemberName(empId) || 'there'
        if (creatorEmpId != null && String(empId) === String(creatorEmpId)) {
          name = task.createdByName || name
        } else if (assigneeEmpId != null && String(empId) === String(assigneeEmpId)) {
          name = this.resolveAssigneeName(assigneeEmpId, task) || name
        }
        recipients.push({
          empId,
          userId: this.taskTeamMemberUserId(empId),
          email,
          name,
        })
      }

      if (!recipients.length) {
        console.warn('[task-email] completion notice skipped — no recipient emails resolved', taskId(task))
        return
      }

      taskService.notifyTaskCompleted({
        task,
        completedByName,
        assigneeName: this.resolveAssigneeName(task.assignedTo, task),
        recipients,
      })
    },
    showPingDeliveryToast({ displayName, pushResult, pingSaved, taskTitle, taskCount }) {
      const pushDelivered = pushResult && Number(pushResult.sent) > 0
      const failureDetail = formatPushFailureMessage(pushResult)

      if (FCM_PUSH_ONLY) {
        if (pushDelivered) {
          let msg
          if (taskTitle) {
            msg = `Notified ${displayName} about "${taskTitle}"`
          } else if (taskCount != null) {
            msg = `Notified ${displayName} on ${taskCount} open task${taskCount === 1 ? '' : 's'}`
          } else {
            msg = `Notified ${displayName}`
          }
          this.toast(msg, 'success', 'BellIcon')
        } else {
          const detail = failureDetail || 'push could not be delivered'
          let msg
          if (pingSaved) {
            msg = `Ping saved on the task, but ${displayName} was not notified — ${detail}. They will not see the bell or a push until server FCM is configured; they can still see a "Pinged" badge on Tasks after refresh.`
          } else {
            msg = `Could not notify ${displayName} — ${detail}.`
          }
          this.toast(msg, 'warning', 'AlertCircleIcon')
        }
        return
      }

      // Legacy mode: in-app poller notifies when ping metadata is saved.
      if (pingSaved) {
        let msg
        if (taskTitle) {
          msg = `Pinged ${displayName} about "${taskTitle}"`
        } else if (taskCount != null) {
          msg = `Pinged ${displayName} on ${taskCount} open task${taskCount === 1 ? '' : 's'}`
        } else {
          msg = `Pinged ${displayName}`
        }
        this.toast(msg, 'success', 'BellIcon')
      }
    },
    /**
     * A manager manually "pinging" a direct report — either about
     * EVERYTHING they have (`TaskListView.vue`'s per-employee name-bell,
     * Team mode, no `task` in the payload) or about ONE specific task
     * (`TaskListView.vue`'s per-row bell / `TaskCard.vue`'s "Ping for
     * update" menu item, both of which DO include `task`) — both paths
     * emit the same `ping` event and land here; `task`'s presence/absence
     * is the ONLY thing that distinguishes them (see each emitter's own
     * doc).
     *
     * Permission: `canPingTask` / `canPingEmployee` (manager of the
     * assignee) — NOT `canEditTaskDetails` / `createdBy`. A manager may
     * ask for updates on any of a direct report's tasks, including ones
     * they did not personally create or assign.
     *
     * Cross-user delivery: (1) persist ping via `PATCH /pm/tasks/:id/ping`
     * when live (else local ping store only); (2) FCM push via
     * `taskService.notifyTaskPing` through POST /api/notify/push (email OFF).
     */
    async handlePingEmployee({ empId, empName, task }) {
      if (empId == null) return
      if (String(empId) === String(this.currentEmpId)) {
        this.toast('You cannot ping yourself for an update.', 'warning', 'BellIcon')
        return
      }
      const displayName = empName || this.taskTeamMemberName(empId) || 'this team member'
      const managerName = `${this.currentUserData?.first_name || ''} ${this.currentUserData?.last_name || ''}`.trim()
        || this.currentUserData?.title || 'Your manager'
      const managerEmpId = this.currentEmpId
      const pingMeta = { empId, managerName, managerEmpId }
      const pushTarget = {
        employeeEmpId: empId,
        employeeUserId: this.taskTeamMemberUserId(empId) || resolveUserIdFromEmpId(empId),
        employeeName: displayName,
        managerName,
        managerEmpId,
        employeeEmail: this.taskTeamMemberEmail(empId),
      }

      if (task) {
        const denial = getPingDenialReason(this.currentUserData, task, this.taskTeamMembers)
        if (denial) {
          this.toast(denial, 'danger', 'AlertCircleIcon')
          return
        }
        const taskTitle = task.title || 'Untitled task'
        const result = await this.persistTaskPingToServer(task, pingMeta)
        this.pingRevision += 1
        if (result.ok) {
          const pushResult = await taskService.notifyTaskPing({
            ...pushTarget,
            taskId: taskId(task),
            taskTitle,
            status: task.status,
            dueDate: task.dueDate,
            sendEmail: false,
          })
          this.showPingDeliveryToast({
            displayName,
            pushResult,
            pingSaved: true,
            taskTitle,
          })
          if (result.stripped) {
            this.toast(
              'Server may not have kept ping data — employee notification is not guaranteed',
              'warning',
              'AlertCircleIcon',
            )
          }
        } else {
          this.toast(
            result.message || 'Could not save ping to the server — employee may not be notified',
            'danger',
            'AlertCircleIcon',
          )
        }
        return
      }

      if (!canPingEmployee(empId, this.taskTeamMembers)) {
        this.toast(
          'Only a manager of this employee can ask them for an update.',
          'danger',
          'AlertCircleIcon',
        )
        return
      }

      // Per-employee ping: write ping metadata onto each open task assigned
      // to them so the employee's poller (any page) can detect it via GET.
      const targets = (this.allTasks || []).filter(t => {
        if (t?.isDeleted) return false
        if (String(taskAssigneeEmpId(t)) !== String(empId)) return false
        return normalizeTaskStatus(t) !== TASK_STATUSES.DONE
      })
      recordEmployeePing({ empId, managerName, managerEmpId })
      this.pingRevision += 1

      if (!targets.length) {
        this.toast(
          `No open tasks for ${displayName} to ping — assign a task first, or ping a specific task`,
          'warning',
          'BellIcon',
        )
        return
      }

      const results = await Promise.all(
        targets.map(t => this.persistTaskPingToServer(t, { ...pingMeta, quiet: true })),
      )
      const okCount = results.filter(r => r && r.ok).length
      const strippedCount = results.filter(r => r && r.ok && r.stripped).length
      if (okCount > 0) {
        const pushResult = await taskService.notifyTaskPing({
          ...pushTarget,
          sendEmail: false,
        })
        this.showPingDeliveryToast({
          displayName,
          pushResult,
          pingSaved: true,
          taskCount: okCount,
        })
        if (strippedCount > 0) {
          this.toast(
            'Server may not have kept ping data — employee notification is not guaranteed',
            'warning',
            'AlertCircleIcon',
          )
        }
      } else {
        const firstMsg = (results.find(r => r && r.message) || {}).message
        this.toast(
          firstMsg || `Could not save ping for ${displayName} — server rejected the update`,
          'danger',
          'AlertCircleIcon',
        )
      }
    },
    /**
     * Persist one task ping so the assignee's session can read it.
     *
     * Uses `canPingTask` (manager of assignee) — never `getEditTaskDetails`.
     * Prefers dedicated `PATCH .../ping`; when not implemented, relies on
     * local ping store only (no PUT fallback — PUT uses edit-details auth).
     *
     * @returns {{ ok: boolean, stripped?: boolean, message?: string }}
     */
    async persistTaskPingToServer(task, { empId, managerName, managerEmpId } = {}) {
      const id = taskId(task)
      if (id == null) return { ok: false, message: 'Missing task id' }

      if (!canPingTask(this.currentUserData, task, this.taskTeamMembers)) {
        return {
          ok: false,
          message: getPingDenialReason(this.currentUserData, task, this.taskTeamMembers)
            || 'Only a manager of the assignee can ask for an update on this task.',
        }
      }

      const taskTitle = task.title || 'Untitled task'
      const record = recordTaskPing({
        taskId: id,
        empId,
        managerName,
        managerEmpId,
        taskTitle,
      })
      if (!record) return { ok: false, message: 'Could not record ping' }

      const pingFields = {
        lastPingedAt: record.lastPingedAt,
        pingCount: record.pingCount,
        lastPingedByName: managerName,
        lastPingedByEmpId: managerEmpId,
        pingSeenAt: null,
      }
      const pingHistoryEntry = buildPingHistoryEntry({
        managerName,
        managerEmpId,
        pingCount: record.pingCount,
        lastPingedAt: record.lastPingedAt,
      })
      const newHistory = appendHistoryEntries(task.changeHistory, [pingHistoryEntry])
      // Ping-only payload — never spread the full task row (title/description/
      // etc.). Full-row PUTs were rejected with the edit-details toast when
      // the manager was not the creator, even though ping is allowed for any
      // manager of the assignee.
      const pingPayload = {
        ...pingFields,
        changeHistory: newHistory,
      }

      this.upsertLocalTask({ ...task, ...pingFields, changeHistory: newHistory })

      const applySaved = (saved, stripped) => {
        if (saved) {
          const savedHistory = normalizeChangeHistory(
            saved.changeHistory ?? saved.change_history,
          )
          this.upsertLocalTask({
            ...task,
            ...saved,
            ...pingFields,
            changeHistory: savedHistory.length ? savedHistory : newHistory,
          })
        }
        return { ok: true, stripped: !!stripped }
      }

      const isEditDetailsDenial = message => typeof message === 'string'
        && /only the task owner or the manager who assigned it can edit/i.test(message)

      try {
        // 1) Dedicated ping route — auth is canPingTask, not canEditTaskDetails.
        const pingRes = await taskService.pingTask(id, pingPayload)
        if (pingRes && pingRes.status === 'OK') {
          const saved = pingRes.data && !Array.isArray(pingRes.data) ? pingRes.data
            : (Array.isArray(pingRes.data) ? pingRes.data[0] : null)
          let stripped = false
          if (saved && !responseRetainsPing(saved, record.lastPingedAt)) {
            stripped = true
            console.warn('[task-ping] ping endpoint stripped ping fields; employee may not see ping', {
              taskId: id,
              keys: saved ? Object.keys(saved) : [],
            })
          }
          return applySaved(saved, stripped)
        }
        if (pingRes && pingRes.status !== 'NOT_IMPLEMENTED' && pingRes.status !== 'SERVICE_UNAVAILABLE') {
          const message = (pingRes && pingRes.message) || 'Could not save ping to the server'
          // Never surface the full-edit denial copy for a ping action.
          if (isEditDetailsDenial(message)) {
            console.warn('[task-ping] ping endpoint incorrectly applied edit-details guard:', message)
            return {
              ok: false,
              message: 'Could not save ping — server rejected manager ask-for-update on this task.',
            }
          }
          console.warn('[task-ping] pingTask rejected:', message)
          return { ok: false, message }
        }

        // 2) PATCH /ping not live — local ping store already written above.
        //    Do NOT fall back to PUT /pm/tasks/:id (403 for non-creators).
        return { ok: true, localOnly: true }
      } catch (err) {
        console.warn('[task-ping] persist error:', err)
        return { ok: false, message: 'Could not save ping to the server' }
      }
    },
    /**
     * Best-effort due-date escalation check — fire-and-forget, never
     * throws, never blocks anything. Runs once after tasks load and again
     * every few minutes while the board stays open (see `created()`).
     *
     * Unlike the invoice module's overdue notifications (server-side cron
     * reading its own `notificationSchedules` config — see
     * `invoice-config-service.js`), the `/pm/tasks` backend has no
     * equivalent scheduling mechanism, so there is no way to send these
     * emails while nobody has the app open. This client-driven check is
     * the realistic alternative for a frontend-only capability: whenever a
     * manager (or the assignee) has the Task board open, overdue tasks get
     * checked and, on first crossing a new tier, emailed — same real
     * `mailService.mailHR()` pipeline as `notifyTaskAssigned`, not a mock.
     *
     * The level itself is ALWAYS the pure, live `getEscalationLevel()`
     * result — this method never stores or reasons about a level itself,
     * only about whether an email for the CURRENT (level, due date)
     * combination has already gone out (`task-escalation-store.js`), so a
     * due-date edit that resolves or changes the level is reflected
     * immediately and never leaves a stale "already sent Final" flag
     * behind for a since-changed due date.
     */
    clearTaskNotificationStateIfComplete(task) {
      if (!isTaskComplete(task)) return
      clearNotifiedLevel(task)
      clearReminderSent(task)
      // Hide stale assignment/escalation rows for this task in the bell.
      try {
        const items = notificationService.noteTaskCompleted(task)
        if (this.$store && this.$store.commit) {
          this.$store.commit('notifications/SET_ITEMS', items)
        }
      } catch (e) {
        // non-blocking
      }
    },
    checkTaskEscalations() {
      this.allTasks.forEach(async task => {
        // Defense in depth — `getEscalationLevel()` already returns null for
        // Done/Completed, soft-deleted, and recurring templates, but never
        // mail about a completed work item even if a stale local row races
        // past that helper.
        if (!task || task.isDeleted) return
        if (taskIsRecurringTemplate(task) && taskRecurringTaskId(task) == null) return
        if (taskRecurringTaskId(task) != null) return
        if (isTaskComplete(task)) return

        const level = getEscalationLevel(task)
        if (!level) return
        const levelIndex = TASK_ESCALATION_LEVELS.findIndex(l => l.key === level)
        const lastNotifiedIndex = getLastNotifiedLevelIndex(task, TASK_ESCALATION_LEVELS)
        if (levelIndex <= lastNotifiedIndex) return

        const assigneeEmpId = task.assignedTo
        const assigneeEmail = this.resolveAssigneeEmail(assigneeEmpId)
        // Calendar-day overdue count (`0` same day after deadline) — see
        // `getDaysOverdue()` in `task-config.js`. Escalation 1 still fires
        // when the deadline passes; the emailed number is not inflated to 1.
        const daysOverdue = getDaysOverdue(task)
        // Manager CC on EVERY tier (escalation_1/2/final): `task.createdBy`
        // → `assignedByEmail`. Missing email → omit CC (no crash). `to`
        // stays the assignee (`assignedTo`).
        const assignedByEmail = await this.resolveManagerEmail(task.createdBy, assigneeEmpId)

        // Re-check after the async manager lookup — status may have flipped
        // to Done while we awaited, and we must not send in that window.
        const live = this.allTasks.find(t => taskId(t) === taskId(task)) || task
        if (isTaskComplete(live) || live.isDeleted) return
        if (taskIsRecurringTemplate(live) && taskRecurringTaskId(live) == null) return
        if (taskRecurringTaskId(live) != null) return
        if (!getEscalationLevel(live)) return

        taskService.notifyTaskEscalation({
          task: live,
          level,
          assigneeEmail,
          assigneeName: this.resolveAssigneeName(assigneeEmpId, live),
          daysOverdue,
          assignedByEmail,
          assigneeEmpId,
          assigneeUserId: this.taskTeamMemberUserId(assigneeEmpId),
          managerEmpId: live.createdBy,
          managerUserId: this.taskTeamMemberUserId(live.createdBy),
        })
        // Recorded regardless of whether `assigneeEmail` resolved — an
        // unresolvable email is logged and skipped inside
        // `notifyTaskEscalation` itself; retrying that same (task, level,
        // due date) combination every 5 minutes forever wouldn't fix it.
        // Push attempts use the same dedup gate (once per level/due date).
        recordNotifiedLevel(live, level)
      })
    },
    /**
     * Gentle due-date reminders for recurring-task OCCURRENCES — replaces
     * the overdue escalation email ladder for those rows. One reminder per
     * (task, dueDate), deduped via `task-reminder-store.js`.
     */
    checkTaskReminders() {
      this.allTasks.forEach(async task => {
        if (!task || task.isDeleted) return
        if (taskRecurringTaskId(task) == null) return
        if (isTaskComplete(task)) return
        if (!taskEligibleForRecurringReminder(task)) return
        if (wasReminderSent(task)) return

        const assigneeEmpId = task.assignedTo
        const assigneeEmail = this.resolveAssigneeEmail(assigneeEmpId)
        const assignedByEmail = await this.resolveManagerEmail(task.createdBy, assigneeEmpId)

        const live = this.allTasks.find(t => taskId(t) === taskId(task)) || task
        if (isTaskComplete(live) || live.isDeleted) return
        if (taskRecurringTaskId(live) == null) return
        if (!taskEligibleForRecurringReminder(live)) return
        if (wasReminderSent(live)) return

        taskService.notifyTaskReminder({
          task: live,
          assigneeEmail,
          assigneeName: this.resolveAssigneeName(assigneeEmpId, live),
          assignedByEmail,
          assigneeEmpId,
          assigneeUserId: this.taskTeamMemberUserId(assigneeEmpId),
          managerEmpId: live.createdBy,
          managerUserId: this.taskTeamMemberUserId(live.createdBy),
        })
        recordReminderSent(live)
      })
    },
    /**
     * Best-effort resolution of the assigning manager's email, used to cc
     * them on ALL THREE escalation tiers (escalation_1/2/final — see
     * `checkTaskEscalations()`; previously final-tier-only). Tries, in
     * cheapest-first order:
     *   1. The manager IS whoever has the board open right now (no lookup).
     *   2. The manager happens to be one of the current user's OWN direct
     *      reports (free — already loaded in `taskTeamMembers`).
     *   3. A real `userService.getUserDetail()` lookup by emp_id — the SAME
     *      call `AttendanceDashboard.vue#checkManagerStatus` already uses
     *      to fetch a manager's record by id, reused here rather than
     *      inventing a new "look up anyone's email" endpoint.
     * Returns `null` (never throws) if the manager can't be resolved
     * (missing `createdBy`, lookup fails, etc.) — the cc is a nice-to-have
     * on top of the escalation email, never a reason to fail/delay it.
     */
    async resolveManagerEmail(managerEmpId, assigneeEmpId) {
      if (managerEmpId == null || String(managerEmpId) === String(assigneeEmpId)) return null
      if (String(managerEmpId) === String(this.currentEmpId)) return this.currentUserData?.email || null
      const teamEmail = this.taskTeamMemberEmail(managerEmpId)
      if (teamEmail) return teamEmail
      try {
        const res = await userService.getUserDetail(managerEmpId)
        if (res && res.status === 'OK' && res.data) return res.data.email || null
      } catch (err) {
        console.warn('[task] manager email lookup failed (non-blocking):', err)
      }
      return null
    },
    /**
     * Best-effort, client-driven recurring-task generator — same reliability
     * caveat and same "runs whenever a user has the board open" model as
     * `checkTaskEscalations()` above (there is no backend scheduler for
     * `/pm/tasks` yet; see the "Recurring Tasks" section of
     * `backend-handoff/TASK_MANAGEMENT_API_DOCUMENTATION.md` for the recommended long-term
     * fix — a real `POST /pm/tasks/recurring` backend job).
     *
     * For every recurring TEMPLATE task (`isRecurring: true`) currently
     * loaded, checks `isRecurrenceDue()` (a pure function of the template's
     * frequency/interval/days/end-date fields and "now" — see
     * `task-config.js`) and, if due, creates exactly ONE new occurrence via
     * the real `createTask()` API — never just pushed into local/Vuex
     * state — then persists the advanced `recurrenceLastGeneratedDate`
     * back onto the template via `updateTask()`, so a page reload (or a
     * different user/device opening the board) sees the SAME "already
     * generated" state and never double-generates.
     *
     * Idempotency (refresh-safe):
     *   1. If an occurrence for `(templateId, nextDueDate)` already exists
     *      in the loaded list → do NOT POST again; only heal the marker.
     *   2. If the series already has any open (non-Done) generated
     *      occurrence → skip (one open card per series for the current
     *      period). Done history is left untouched.
     *
     * Dedup is therefore (a) the existence of the occurrence row itself,
     * plus (b) the SERVER-PERSISTED marker on the template. Marker-only
     * dedup was insufficient when the backend ignored/stripped
     * `recurrenceLastGeneratedDate` — every refresh re-POSTed. 
     * `recurringGenerationInFlight` remains a same-tab race guard only.
     *
     * Only ONE occurrence is generated per template per check, even if
     * several are overdue at once (e.g. the board wasn't opened for a
     * week) — see `isRecurrenceDue()`'s doc. A significantly-behind
     * template simply catches up one occurrence per 5-minute interval tick
     * (or per page load) rather than all at once — and only when the
     * previous open occurrence has been completed.
     */
    checkRecurringTasks() {
      this.allTasks.forEach(template => {
        if (!taskIsRecurringTemplate(template) || template.isDeleted) return
        // Occurrences carry `recurringTaskId` back to the template — never
        // treat them as generators themselves (even if a buggy payload left
        // `isRecurring: true` on the child row).
        if (taskRecurringTaskId(template) != null) return
        const id = taskId(template)
        if (id == null || this.recurringGenerationInFlight.has(id)) return
        if (!isRecurrenceDue(template)) return

        const nextDate = computeNextRecurrenceDate(template)
        const nextDueDate = formatDateOnly(nextDate)
        if (!nextDueDate) return

        // Already materialized for this due date (Done or open) → heal
        // marker only. This is what stops "new card on every refresh"
        // when the marker failed to persist previously.
        const existingForDue = findRecurringOccurrenceForDueDate(this.allTasks, id, nextDueDate)
        if (existingForDue) {
          this.recurringGenerationInFlight.add(id)
          this.advanceRecurrenceMarker(template, nextDueDate)
            .finally(() => this.recurringGenerationInFlight.delete(id))
          return
        }

        // One open occurrence per series — wait until the current open
        // card is Done before spawning the next period.
        if (hasOpenRecurringOccurrence(this.allTasks, id)) return

        this.recurringGenerationInFlight.add(id)
        this.generateRecurringOccurrence(template, nextDueDate)
          .finally(() => this.recurringGenerationInFlight.delete(id))
      })
    },
    /**
     * Persist `recurrenceLastGeneratedDate` on the template (full-body PUT
     * + camelCase/snake_case aliases). Used after a successful create AND
     * when healing a stuck marker after finding an existing occurrence.
     * Never throws.
     */
    async advanceRecurrenceMarker(template, nextDueDate) {
      const templateId = taskId(template)
      if (templateId == null || !nextDueDate) return
      const { id: _unusedTemplateId, ...templateFields } = template
      // Send both casings — some backends only persist snake_case columns
      // and would otherwise leave the marker looking unset on the next GET,
      // which historically caused a fresh POST on every board refresh.
      const markerBody = {
        ...templateFields,
        recurrenceLastGeneratedDate: nextDueDate,
        recurrence_last_generated_date: nextDueDate,
        isRecurring: true,
        is_recurring: true,
      }
      // Only the template creator may PUT — managers viewing team tasks must
      // not spam 403s on load when healing the recurrence marker locally.
      if (canConfigureRecurrence(this.currentUserData, template)) {
        try {
          const markerRes = await taskService.updateTask(templateId, markerBody)
          if (markerRes && markerRes.status === 'ERROR') {
            console.warn('[task] recurring marker update failed — occurrence existence gate still prevents re-POST:', markerRes.message)
          }
        } catch (err) {
          console.warn('[task] recurring marker update threw (non-blocking):', err)
        }
      }
      this.upsertLocalTask({
        ...template,
        recurrenceLastGeneratedDate: nextDueDate,
        recurrence_last_generated_date: nextDueDate,
      })
    },
    /** The actual create-plus-advance-marker round-trip for one occurrence — split out of `checkRecurringTasks()` purely so that method can stay a synchronous `forEach` while this part awaits the two API calls. Never throws — API failures are toasted; successful background generation is silent (console only), since this runs on every board load and on a 5-minute interval. Deliberately does NOT advance `recurrenceLastGeneratedDate` on failure, so the same occurrence is retried on the next check instead of silently being skipped forever. */
    async generateRecurringOccurrence(template, nextDueDate) {
      const templateId = taskId(template)

      // Re-check immediately before POST (interval / double-invoke safety).
      if (findRecurringOccurrenceForDueDate(this.allTasks, templateId, nextDueDate)) {
        await this.advanceRecurrenceMarker(template, nextDueDate)
        return
      }
      if (hasOpenRecurringOccurrence(this.allTasks, templateId)) return

      const data = buildTaskPayload({
        title: template.title,
        description: template.description,
        status: undefined, // omit -> buildTaskPayload defaults to DEFAULT_TASK_STATUS; a generated occurrence always starts fresh, never inheriting the template's current column.
        priority: template.priority,
        assignedTo: template.assignedTo,
        assignedToName: template.assignedToName,
        dueDate: nextDueDate,
        // Carried over from the template so each occurrence keeps the
        // SAME time-of-day deadline `isRecurrenceDue()` used to decide
        // this occurrence was ready to generate in the first place (see
        // its doc in `task-config.js`) — previously omitted entirely,
        // which silently reset every generated occurrence back to the
        // (potentially different) 6 PM IST default regardless of what
        // due time the template itself had configured.
        dueTime: template.dueTime,
        createdBy: template.createdBy,
        createdByName: template.createdByName,
        // The occurrence itself is a plain, one-off task — recurrence
        // fields stay on the template only; `isRecurring` left `false`
        // (the default) by simply not setting it here.
        recurringTaskId: templateId,
      }, this.currentUserData)

      try {
        const res = await taskService.createTask(data)
        if (res && res.status === 'ERROR') {
          this.showErrorToast(res.message || `Failed to auto-generate the next occurrence of "${template.title}".`)
          return
        }
        const created = normalizeTaskRecurrenceFields({
          ...data,
          id: extractServerId(res) || `local-${Date.now()}`,
        })
        this.allTasks.push(created)

        // Marker advance after create — even if this PUT fails, the
        // existence gate above will refuse to POST the same due date again
        // on the next refresh (it will only retry advancing the marker).
        await this.advanceRecurrenceMarker(template, nextDueDate)
        console.info('[task] recurring occurrence generated:', template.title, nextDueDate)
        this.notifyAssignment(created, data.assignedTo)
      } catch (err) {
        console.error('[task] recurring generation error:', err)
        this.showErrorToast(`Could not auto-generate the next occurrence of "${template.title}". Will retry.`)
      }
    },
    upsertLocalTask(updated) {
      const id = taskId(updated)
      const index = this.allTasks.findIndex(t => taskId(t) === id)
      if (index >= 0) {
        this.$set(this.allTasks, index, { ...this.allTasks[index], ...updated })
      } else {
        this.allTasks.push(updated)
      }
    },
    /**
     * Fire-and-forget task edit-history recorder — see the "Task Edit
     * History" section of `backend-handoff/TASK_MANAGEMENT_API_DOCUMENTATION.md` for the
     * full contract. `previousTask` is the task's state BEFORE this save;
     * `updatedFields` is whatever NEW field values were just written (a
     * full `buildTaskPayload()` result for a full edit, or a small partial
     * object like `{ priority }` for a quick action — see
     * `buildHistoryEntries()`'s doc for why only fields actually present in
     * `updatedFields` are ever diffed/recorded).
     *
     * Tries the dedicated `POST /pm/tasks/:id/history` endpoint first
     * (`taskService.addTaskHistoryEntries`); when that comes back
     * `NOT_IMPLEMENTED` (true for the live backend as of this writing — see
     * `task-service.js`), gracefully degrades to accumulating a
     * `changeHistory` array directly on the task row via the general
     * `updateTask()` — the pragmatic fallback the feature spec asked for.
     * Never throws and is never awaited by its callers, same
     * never-blocks-the-UI resilience contract as `notifyAssignment()`
     * above — losing a history entry to a transient network blip is far
     * preferable to delaying/failing the actual task save it's describing.
     */
    async recordTaskHistory(id, previousTask, updatedFields) {
      if (id == null || !previousTask || !updatedFields) return
      const entries = buildHistoryEntries(previousTask, updatedFields, this.currentUserData)
      if (!entries.length) return

      // Push to task manager on every change (completion → email instead) —
      // fired off the SAME diff just built for the History panel, for EVERY
      // mutating path that calls this method. Fire-and-forget; never blocks
      // history persistence below.
      const currentTaskForEmail = this.allTasks.find(t => taskId(t) === id) || previousTask
      this.notifyTaskUpdated(currentTaskForEmail, entries)

      try {
        const res = await taskService.addTaskHistoryEntries(id, entries)
        if (res && res.status === 'NOT_IMPLEMENTED') {
          const currentTask = this.allTasks.find(t => taskId(t) === id) || previousTask
          const newHistory = appendHistoryEntries(currentTask.changeHistory, entries)
          this.upsertLocalTask({ id, changeHistory: newHistory })
          // Server history route missing — persist locally only unless this
          // user may edit the task (PUT uses creator-only auth on the backend).
          if (canEditTaskDetails(this.currentUserData, currentTask)) {
            // Merge onto the FULL current row (not just `{ changeHistory }`
            // alone) before sending — `updateTask()`'s general `PUT` has
            // historically only ever been exercised with a complete task
            // payload (see its own doc in `task-service.js`), so a bare
            // partial body here risks the backend treating missing fields as
            // "clear them" if it doesn't already support sparse patches.
            const { id: _unusedId, ...fallbackFields } = currentTask
            const fallbackRes = await taskService.updateTask(id, { ...fallbackFields, changeHistory: newHistory })
            if (fallbackRes && fallbackRes.status === 'ERROR') {
              console.warn('[task] history fallback persist failed (non-blocking):', fallbackRes.message)
            }
          }
        } else if (res && res.status === 'ERROR') {
          console.warn('[task] history record failed (non-blocking):', res.message)
        }
      } catch (err) {
        console.warn('[task] history record error (non-blocking):', err)
      }
    },
    /**
     * Persist a create/update, using multipart when a file is attached (or
     * an existing attachment is cleared). If the server rejects the file
     * part (multer not wired yet), retries JSON-only and keeps a local
     * blob preview via `mergeTaskAttachmentFields` — task fields still save.
     */
    async persistTaskWithOptionalAttachment(data, { id = null, file = null, removeAttachment = false } = {}) {
      const wantsMultipart = !!(file || removeAttachment)
      if (!wantsMultipart) {
        return id != null
          ? taskService.updateTask(id, data)
          : taskService.createTask(data)
      }
      const formData = buildTaskFormData(data, { file, removeAttachment })
      let res = id != null
        ? await taskService.updateTask(id, formData)
        : await taskService.createTask(formData)
      if (res && res.status === 'OK') return res
      const msg = (res && res.message) || ''
      // Only retry JSON when the server specifically rejected the file part
      // (multer not wired). Other errors (validation/auth) must surface as-is.
      const isMulterReject = res
        && res.status === 'ERROR'
        && /multer|Unexpected field|does not accept file|File upload failed/i.test(msg)
      if (!isMulterReject) return res
      console.warn('[task] multipart save rejected — retrying JSON without file binary:', msg)
      res = id != null
        ? await taskService.updateTask(id, data)
        : await taskService.createTask(data)
      if (res && res.status === 'OK' && file) {
        this.toast('Task saved. File kept as local preview until the server accepts uploads.', 'warning', 'PaperclipIcon')
      }
      return res
    },
    async handleSubmit(payload) {
      this.submitting = true
      try {
        if (payload.mode === 'project-status-only') {
          const ok = await this.applyProjectStatusUpdate(payload)
          if (ok) this.showFormModal = false
          return
        }

        if (payload.mode === 'responsibility') {
          const existing = this.allTasks.find(t => taskId(t) === payload.id)
          if (!existing || !isProjectResponsibilityTask(existing)) {
            this.showErrorToast('Project responsibility not found.')
            return
          }
          if (payload.statusChanged) {
            const denial = getStatusDenialReason(this.currentUserData, existing)
            if (denial) {
              this.showErrorToast(denial)
              return
            }
            const done = normalizeTaskStatus({ status: payload.status }) === TASK_STATUSES.DONE
            const wasDone = normalizeTaskStatus(existing) === TASK_STATUSES.DONE
            const updatedTask = {
              ...existing,
              status: payload.status,
              completedAt: done ? new Date().toISOString() : null,
            }
            this.upsertLocalTask(updatedTask)
            this.clearTaskNotificationStateIfComplete(updatedTask)
            await this.syncMemberResponsibilityFromTask(existing, done)
            if (done && !wasDone) this.notifyTaskCompleted(updatedTask)
            this.toast(done ? 'Project responsibility marked done' : 'Project responsibility reopened', 'success', 'CheckCircleIcon')
          }
          if (payload.projectStatusUpdate) {
            const ok = await this.applyProjectStatusUpdate(payload.projectStatusUpdate)
            if (!ok && !payload.statusChanged) return
          }
          this.showFormModal = false
          return
        }

        if (payload.mode === 'priority-only') {
          const existing = this.allTasks.find(t => taskId(t) === payload.id)
          const denial = getPriorityDenialReason(this.currentUserData, existing, this.taskTeamMembers)
          if (denial) {
            this.showErrorToast(denial)
            return
          }
          const res = await taskService.updateTaskPriority(payload.id, payload.priority)
          if (res && res.status === 'ERROR') {
            this.showErrorToast(res.message || 'Failed to update priority.')
            return
          }
          // NOTE: we intentionally do NOT merge `res` into the task here —
          // the backend's response shape is unknown/unverified (see final
          // report), and a `{ status: 'OK', ... }` envelope's `status` key
          // would collide with our own domain `status` (Kanban column)
          // field. Our own payload is the source of truth for what we just
          // told the server to persist.
          this.upsertLocalTask({ ...existing, priority: payload.priority })
          this.recordTaskHistory(payload.id, existing, { priority: payload.priority })
          if (payload.projectStatusUpdate) {
            await this.applyProjectStatusUpdate(payload.projectStatusUpdate)
          }
          this.toast('Priority updated')
          this.showFormModal = false
          return
        }

        if (payload.mode === 'status-only') {
          // Assignee-only edit from the modal (task was assigned by someone
          // else) — mirrors the priority-only branch above: submit ONLY the
          // status change via the same endpoint the drag-and-drop status
          // move uses, never touching fields this user can't edit.
          const existing = this.allTasks.find(t => taskId(t) === payload.id)
          const denial = getStatusDenialReason(this.currentUserData, existing)
          if (denial) {
            this.showErrorToast(denial)
            return
          }
          const projectDenial = getProjectStatusDenialReason(
            this.currentUserData,
            this.projectForTask(existing),
            existing,
            payload.status,
          )
          if (projectDenial) {
            this.showErrorToast(projectDenial)
            return
          }
          const completedAt = deriveCompletedAt(existing, payload.status)
          const res = await taskService.updateTaskStatus(payload.id, payload.status, { completedAt })
          if (res && res.status === 'ERROR') {
            this.showErrorToast(res.message || 'Failed to update status.')
            return
          }
          const statusUpdated = { ...existing, status: payload.status, completedAt }
          this.upsertLocalTask(statusUpdated)
          this.clearTaskNotificationStateIfComplete(statusUpdated)
          this.recordTaskHistory(payload.id, existing, { status: payload.status })
          if (payload.projectStatusUpdate) {
            await this.applyProjectStatusUpdate(payload.projectStatusUpdate)
          }
          this.toast(`Moved to ${payload.status}`, 'success', 'ArrowRightCircleIcon')
          this.showFormModal = false
          return
        }

        if (payload.mode === 'edit') {
          const existing = this.allTasks.find(t => taskId(t) === payload.id)
          const denial = getEditDenialReason(this.currentUserData, existing)
          if (denial) {
            this.showErrorToast(denial)
            return
          }
          if (payload.status !== normalizeTaskStatus(existing)) {
            const projectDenial = getProjectStatusDenialReason(
              this.currentUserData,
              this.projectForTask(existing),
              existing,
              payload.status,
            )
            if (projectDenial) {
              this.showErrorToast(projectDenial)
              return
            }
          }
          // Preserve the ORIGINAL creator/assignee display metadata — the
          // edit form only lets the user change title/description/status/
          // priority/dueDate/assignedTo, so anything it didn't touch must
          // come from `existing`, not be silently re-stamped as "me".
          // `completedAt` is likewise derived from `existing`'s PREVIOUS
          // status, not guessed fresh — see `deriveCompletedAt()`'s doc.
          const data = buildTaskPayload({
            ...payload,
            createdBy: existing?.createdBy,
            createdByName: existing?.createdByName,
            assignedToName: this.resolveAssigneeName(payload.assignedTo, existing),
            completedAt: deriveCompletedAt(existing, payload.status),
            // Not a form field (see `TaskFormModal.vue`'s `blankForm()` doc)
            // — preserve whatever generation progress the template already
            // had rather than letting an unrelated edit reset it back to
            // the (possibly long-past) due date, which would make
            // `checkRecurringTasks()` think every occurrence since is
            // suddenly "due" again. Seeds fresh (from THIS save's due date)
            // only the first time a task is turned into a recurring
            // template — i.e. it had no prior recurrence state at all.
            recurrenceLastGeneratedDate: payload.isRecurring
              ? (existing?.recurrenceLastGeneratedDate || payload.dueDate)
              : null,
            recurringTaskId: existing?.recurringTaskId ?? null,
          }, this.currentUserData)
          const file = payload.attachmentFile || null
          const removeAttachment = !!payload.removeAttachment && !file
          const res = await this.persistTaskWithOptionalAttachment(data, {
            id: payload.id,
            file,
            removeAttachment,
          })
          if (res && res.status === 'ERROR') {
            this.showErrorToast(res.message || 'Failed to update task.')
            return
          }
          const serverRow = (res && res.data && !Array.isArray(res.data)) ? res.data : null
          const merged = mergeTaskAttachmentFields(
            { ...existing, ...data, id: payload.id },
            { file, removeAttachment, serverRow },
          )
          this.upsertLocalTask(merged)
          this.clearTaskNotificationStateIfComplete(merged)
          this.recordTaskHistory(payload.id, existing, data)
          if (payload.projectStatusUpdate) {
            await this.applyProjectStatusUpdate(payload.projectStatusUpdate)
          }
          this.toast('Task updated')
          this.showFormModal = false
          // Only notify when the assignee actually CHANGED via this edit —
          // re-notifying on every unrelated field edit would be noisy.
          if (existing && String(existing.assignedTo) !== String(data.assignedTo)) {
            this.notifyAssignment(merged, data.assignedTo)
          }
          return
        }

        if (payload.mode === 'create-multi') {
          // "Assign to multiple?" (`TaskFormModal.vue`) — Option A design:
          // clone the EXACT same single-task creation sequence used by the
          // plain "create" branch below (`buildTaskPayload()` ->
          // `taskService.createTask()` -> push to `allTasks` ->
          // `notifyAssignment()`), once per selected emp_id, all sharing
          // one freshly-generated `groupId` — see "Multi-Assignee Tasks" in
          // `backend-handoff/TASK_MANAGEMENT_API_DOCUMENTATION.md`. No new endpoint/service
          // method — `taskService.createTask()` is reused as-is, in a loop.
          const empIds = Array.from(new Set((payload.assignedToList || []).map(String)))
          if (!empIds.length) {
            this.showErrorToast('Select at least one person to assign this task to.')
            return
          }
          if (payload.projectId != null) {
            const project = this.projects.find(p => String(getProjectId(p)) === String(payload.projectId))
            if (project && !canCreateTaskInProject(this.currentUserData, project)) {
              this.showErrorToast('Your project role does not allow creating tasks in this project.')
              return
            }
          }
          const groupId = `group-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
          // Resolved once up front (not once per clone) — every clone's
          // static `coAssignees` roster (see `coAssigneesFor()`'s doc in
          // `task-config.js`) needs every OTHER selected person's name,
          // not just its own assignee's.
          const namesByEmpId = {}
          empIds.forEach(empId => { namesByEmpId[empId] = this.resolveAssigneeName(empId, null) })

          let createdCount = 0
          // Sequential, not `Promise.all` — mirrors doing this "by hand"
          // once per person (simpler to reason about/debug if one clone's
          // create call fails, and avoids hammering the mail-sending
          // backend with N simultaneous assignment-email requests).
          for (let i = 0; i < empIds.length; i += 1) {
            const empId = empIds[i]
            const coAssignees = empIds
              .filter(id => id !== empId)
              .map(id => ({ empId: id, name: namesByEmpId[id] }))
            const data = buildTaskPayload({
              ...payload,
              assignedTo: empId,
              assignedToName: namesByEmpId[empId],
              groupId,
              coAssignees,
              recurrenceLastGeneratedDate: payload.isRecurring ? payload.dueDate : null,
              // eslint-disable-next-line no-await-in-loop
            }, this.currentUserData)
            const file = payload.attachmentFile || null
            // eslint-disable-next-line no-await-in-loop
            const res = await this.persistTaskWithOptionalAttachment(data, { file })
            if (res && res.status === 'ERROR') {
              this.showErrorToast(`Failed to create task for ${namesByEmpId[empId] || empId}. ${res.message || ''}`.trim())
              continue // eslint-disable-line no-continue
            }
            const serverRow = (res && res.data && !Array.isArray(res.data)) ? res.data : null
            const clone = mergeTaskAttachmentFields(
              { ...data, id: extractServerId(res) || `local-${Date.now()}-${empId}` },
              { file, serverRow },
            )
            this.allTasks.push(clone)
            createdCount += 1
            // Same fire-and-forget contract as the single-assignee path —
            // one individual assignment email per clone, unchanged.
            this.notifyAssignment(clone, empId)
          }
          if (createdCount) {
            this.toast(`Task created for ${createdCount} ${createdCount === 1 ? 'person' : 'people'}`)
          }
          this.showFormModal = false
          return
        }

        // create
        if (payload.projectId != null) {
          const project = this.projects.find(p => String(getProjectId(p)) === String(payload.projectId))
          if (project && !canCreateTaskInProject(this.currentUserData, project)) {
            this.showErrorToast('Your project role does not allow creating tasks in this project.')
            return
          }
        }
        const data = buildTaskPayload({
          ...payload,
          assignedToName: this.resolveAssigneeName(payload.assignedTo, null),
          // A brand-new recurring template's own due date counts as
          // "occurrence #1" — see `computeNextRecurrenceDate()`'s doc in
          // `task-config.js` — so the next occurrence is computed relative
          // to it from the very start, with no separate "first run" case
          // needed in the generator.
          recurrenceLastGeneratedDate: payload.isRecurring ? payload.dueDate : null,
        }, this.currentUserData)
        const file = payload.attachmentFile || null
        const res = await this.persistTaskWithOptionalAttachment(data, { file })
        if (res && res.status === 'ERROR') {
          this.showErrorToast(res.message || 'Failed to create task.')
          return
        }
        const serverRow = (res && res.data && !Array.isArray(res.data)) ? res.data : null
        const created = mergeTaskAttachmentFields(
          { ...data, id: extractServerId(res) || `local-${Date.now()}` },
          { file, serverRow },
        )
        this.allTasks.push(created)
        this.toast('Task created')
        this.showFormModal = false
        this.notifyAssignment(created, data.assignedTo)
      } catch (err) {
        console.error('[task] submit error:', err)
        this.showErrorToast('Something went wrong. Please try again.')
      } finally {
        this.submitting = false
      }
    },
    async handleChangePriority({ task, priority }) {
      const denial = getPriorityDenialReason(this.currentUserData, task, this.taskTeamMembers)
      if (denial) {
        this.showErrorToast(denial)
        return
      }
      const id = taskId(task)
      this.upsertLocalTask({ ...task, priority })
      this.recordTaskHistory(id, task, { priority })
      try {
        const res = await taskService.updateTaskPriority(id, priority)
        if (res && res.status === 'ERROR') {
          this.showErrorToast(`${res.message || 'Priority change may not have saved to the server.'}`)
        } else {
          this.toast(`Priority set to ${priority}`)
        }
      } catch (err) {
        console.error('[task] priority update error:', err)
        this.showErrorToast('Priority change may not have saved to the server.')
      }
    },
    async handleStatusChange({ task, newStatus, orderedIds }) {
      const id = taskId(task)
      if (isProjectResponsibilityTask(task)) {
        const wasDone = normalizeTaskStatus(task) === TASK_STATUSES.DONE
        const done = normalizeTaskStatus({ status: newStatus }) === TASK_STATUSES.DONE
        const updatedTask = {
          ...task,
          status: newStatus,
          completedAt: done ? new Date().toISOString() : null,
        }
        this.upsertLocalTask(updatedTask)
        this.clearTaskNotificationStateIfComplete(updatedTask)
        setColumnOrder(orderedIds)
        await this.syncMemberResponsibilityFromTask(task, done)
        if (done && !wasDone) this.notifyTaskCompleted(updatedTask)
        this.toast(done ? 'Project responsibility marked done' : 'Project responsibility reopened', 'success', 'CheckCircleIcon')
        return
      }
      const project = this.projectForTask(task)
      const projectDenial = getProjectStatusDenialReason(this.currentUserData, project, task, newStatus)
      if (projectDenial) {
        this.showErrorToast(projectDenial)
        return
      }
      // `task` here is still its PREVIOUS state (the drag hasn't been
      // persisted anywhere yet) — exactly what `deriveCompletedAt()` needs
      // to tell "just became Done" apart from "already was Done".
      const completedAt = deriveCompletedAt(task, newStatus)
      const updatedTask = { ...task, status: newStatus, completedAt }
      this.upsertLocalTask(updatedTask)
      this.clearTaskNotificationStateIfComplete(updatedTask)
      this.recordTaskHistory(id, task, { status: newStatus })
      setColumnOrder(orderedIds)
      try {
        const res = await taskService.updateTaskStatus(id, newStatus, { completedAt })
        if (res && res.status === 'ERROR') {
          this.showErrorToast(res.message || 'Status change may not have saved to the server.')
        } else {
          this.toast(`Moved to ${newStatus}`, 'success', 'ArrowRightCircleIcon')
        }
      } catch (err) {
        console.error('[task] status update error:', err)
        this.showErrorToast('Status change may not have saved to the server.')
      }
    },
    /**
     * Cross-column drag in the per-member "My Team's Tasks" board —
     * reassigns the task to the target member instead of changing its
     * status. Re-checks permission defensively (mirrors `handleChangePriority`
     * /`handleStatusChange`) even though `TaskKanbanBoard`'s `onMove` guard
     * already should have blocked an unauthorized drop before it landed here.
     */
    async handleReassign({ task, newAssigneeEmpId, orderedIds }) {
      const denial = getReassignDenialReason(this.currentUserData, task, this.taskTeamMembers, newAssigneeEmpId)
      if (denial) {
        this.showErrorToast(denial)
        return
      }
      const id = taskId(task)
      const newAssigneeName = this.resolveAssigneeName(newAssigneeEmpId, task)
      const updatedTask = { ...task, assignedTo: String(newAssigneeEmpId), assignedToName: newAssigneeName }
      this.upsertLocalTask(updatedTask)
      this.recordTaskHistory(id, task, { assignedTo: String(newAssigneeEmpId), assignedToName: newAssigneeName })
      setColumnOrder(orderedIds)
      try {
        const res = await taskService.updateTaskAssignee(id, newAssigneeEmpId, { assignedToName: newAssigneeName })
        if (res && res.status === 'ERROR') {
          this.showErrorToast(res.message || 'Reassignment may not have saved to the server.')
        } else {
          this.toast(`Reassigned to ${newAssigneeName || 'team member'}`, 'success', 'UserCheckIcon')
          this.notifyAssignment(updatedTask, newAssigneeEmpId)
        }
      } catch (err) {
        console.error('[task] reassign error:', err)
        this.showErrorToast('Reassignment may not have saved to the server.')
      }
    },
    projectForTask(task) {
      const pid = task?.projectId ?? task?.project_id
      if (pid == null) return null
      return this.projects.find(p => String(getProjectId(p)) === String(pid)) || null
    },
    handleReorder({ orderedIds }) {
      // Ordering is a client-side UX concern (see task-order-store.js) — the
      // localStorage write already happened in TaskKanbanBoard; best-effort
      // mirror to the backend `order` field, silently ignored on failure.
      setColumnOrder(orderedIds)
      const pairs = orderedIds.map((id, index) => ({ id, order: index }))
      taskService.reorderTasks(pairs).catch(err => console.warn('[task] reorder sync failed:', err))
    },
  },
}
</script>

<style lang="scss">
@import "@core/scss/vue/apps/task.scss";
</style>
